import { QUESTION_TYPES } from "@zenforge/shared";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, uploadFile, useApi, usePendingActions } from "../api.js";
import {
  ActionButton,
  FullScreenLoader,
  ModelSelect,
  Notice,
  formatCost,
  formatDuration,
  useToast,
} from "../components.js";
import { useStickyState } from "../useStickyState.js";
import { ContentPreview } from "../ContentPreview.js";
import { PdfViewerModal } from "../PdfViewerModal.js";
import { DocxViewerModal } from "../DocxViewerModal.js";
import { usePermission } from "../auth-context.js";


type ResponseType = "html" | "json" | "markdown";
type SampleMode = "none" | "exemplar" | "template";
type IndexingStatus = "pending" | "uploading" | "indexing" | "ready" | "failed";

interface Category {
  id: number;
  collectionId: number;
  name: string;
  code: string;
  categoryType?: "MODULE" | "BOOK_IN" | "BOOK_BACK";
  /** Null when instructions come from an attached .docx instead. */
  prompt: string | null;
  responseType: ResponseType;
  outputSchema: Record<string, unknown> | unknown[] | null;
  sampleOutput: string | null;
  sampleMode: SampleMode;
  isActive: boolean;
  /** The main app's module.id this category writes content into on Approve. */
  externalModuleId: string | null;
  /** An optional .docx attached to the prompt (migration 025). Sent to the
   *  model as an input_file on every generation. */
  promptDocFileId: string | null;
  promptDocFilename: string | null;
  promptDocSizeBytes: number | null;
  /** AI critique pass — off by default. Distinct from the human review
   *  workflow (Content Review, reviewedContent) elsewhere in the app. */
  critiqueEnabled: boolean;
  critiquePrompt: string | null;
  critiqueResponseType: "text" | "json";
  critiqueOutputSchema: Record<string, unknown> | unknown[] | null;
  /** Question-target settings — used only when code is `book_in_questions`
   *  (migration 028). Null on every other category. */
  questionAssessmentType: string | null;
  questionMark: number | null;
  questionImportance: number | null;
  questionCategoryId: string | null;
}

/** One book-back category from the main app, for the picker. */
interface QuestionCategoryOption {
  id: string;
  name: string;
  assessmentType: string;
  sequenceNo: number;
}

interface MainAppModule {
  id: string;
  name: string;
  moduleType: string;
}

export interface FileDeleteModalState {
  title: string;
  filename: string;
  onConfirm: (purgeRemote: boolean) => Promise<void>;
}

export function FileRemovalModal({
  modal,
  onClose,
}: {
  modal: FileDeleteModalState;
  onClose: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        style={{ maxWidth: 540, width: "90%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row between mb-4" style={{ marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>{modal.title}</h3>
          <button type="button" className="btn mini" onClick={onClose} disabled={submitting}>
            ✕
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: "0.95rem" }}>
            File: <b>{modal.filename}</b>
          </div>
          <p className="mini muted" style={{ marginTop: 6, lineHeight: 1.45 }}>
            Please select how you want to remove this file. If this pipeline or collection was cloned from another, removing from DB only preserves the file for cloned/original pipelines sharing it.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            className="btn primary"
            disabled={submitting}
            style={{
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "12px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              cursor: submitting ? "not-allowed" : "pointer",
            }}
            onClick={async () => {
              setSubmitting(true);
              try {
                await modal.onConfirm(false);
              } finally {
                setSubmitting(false);
                onClose();
              }
            }}
          >
            <div style={{ fontWeight: 600 }}>📂 Remove from DB Only (Recommended for Clones)</div>
            <div className="mini" style={{ opacity: 0.85, fontWeight: 400 }}>
              Unlinks the file reference from this database row. Leaves the OpenAI Vector Store / Files API file untouched so other cloned pipelines continue working.
            </div>
          </button>

          <button
            type="button"
            className="btn danger"
            disabled={submitting}
            style={{
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "12px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              cursor: submitting ? "not-allowed" : "pointer",
            }}
            onClick={async () => {
              setSubmitting(true);
              try {
                await modal.onConfirm(true);
              } finally {
                setSubmitting(false);
                onClose();
              }
            }}
          >
            <div style={{ fontWeight: 600 }}>💥 Remove from Both DB & OpenAI Vector Store</div>
            <div className="mini" style={{ opacity: 0.85, fontWeight: 400 }}>
              Permanently purges the file from OpenAI's Vector Store and Files API.
            </div>
          </button>
        </div>

        <div className="row end" style={{ marginTop: 18 }}>
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** The syllabus hierarchy, same shape the Generation screen fetches. */
interface TreeTopic {
  id: string;
  name: string;
  sequenceNo: number | null;
}

interface TreeSubChapter {
  id: string;
  name: string;
  sequenceNo: number | null;
  topics: TreeTopic[];
}

interface TreeChapter {
  id: string;
  name: string;
  sequenceNo: number | null;
  subChapters: TreeSubChapter[];
}

interface ChapterTree {
  chapters: TreeChapter[];
}

/** One PDF covers one chapter (optionally one subchapter), so this array
 *  holds at most one row — the join table can hold more, nothing writes it. */
interface ScopeRow {
  id: number;
  chapterId: string;
  subChapterId: string | null;
  /** Set only for a PDF covering one topic (migration 022). */
  topicId: string | null;
  /** Set only for a chapter-level PDF dedicated to one Book Back category
   *  (migration 058). */
  categoryId: number | null;
}

interface KnowledgeSourceRow {
  id: number;
  filename: string;
  sizeBytes: number | null;
  roleTag?: string;
  /** How generation reads this file (migration 053). Absent on rows that
   *  predate it, which are all searched. */
  retrievalMode?: string;
  indexingStatus: IndexingStatus;
  errorMessage: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** Chosen when the PDF was added (migration 047). Recorded only — no
   *  generation path reads these yet. */
  defaultModel?: string | null;
  defaultReasoningEffort?: "low" | "medium" | "high" | null;
  /** Joined, so a renamed user shows their current name. Absent on sources
   *  uploaded before migration 046, and on deleted users. */
  createdByUser?: { id: number; name: string } | null;
  updatedByUser?: { id: number; name: string } | null;
  /** False for anything indexed before knowledge-source filtering existed —
   *  it can't be scoped to until it's re-uploaded. */
  filterable: boolean;
  scopes?: ScopeRow[];
}

interface CollectionDetailData {
  id: number;
  name: string;
  description: string | null;
  model: string;
  vectorStoreId: string | null;
  /** Name of the OPENAI_API_KEYS entry in use; null is the default key. */
  openaiKeyName: string | null;
  allowGeneralKnowledge: boolean;
  archivedAt: string | null;
  /** The one syllabus this collection covers (migration 020) — the chapter
   *  list below comes from it, so there is nothing to pick here. */
  board: string | null;
  className: string | null;
  subject: string | null;
  knowledgeSources: KnowledgeSourceRow[];
  categories: Category[];
}

interface RunResult {
  content: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  durationMs: number;
  retrievedChunks: number;
  searchCalls: number;
  notInTextbook: boolean;
  ungrounded: boolean;
  estimatedCost: number | null;
  critique: {
    feedback: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    estimatedCost: number | null;
    durationMs: number;
  } | null;
  critiqueError: string | null;
}

/** One persisted row from GET /collections/:id/run — a past test, kept purely
 *  as a spend/history log (see migration 012). Never reviewable or
 *  approvable, unlike a real generated_contents row. */
interface TestRunRow {
  id: number;
  categoryId: number;
  target: string;
  content: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  // DECIMAL columns round-trip through MySQL as strings, not numbers — same
  // gotcha JobRow/ContentRow already guard against above.
  estimatedCost: string | number | null;
  durationMs: number;
  retrievedChunks: number | null;
  searchCalls: number | null;
  notInTextbook: boolean;
  ungrounded: boolean;
  critiqueFeedback: string | null;
  critiqueModel: string | null;
  critiqueInputTokens: number | null;
  critiqueOutputTokens: number | null;
  critiqueEstimatedCost: string | number | null;
  critiqueDurationMs: number | null;
  createdAt: string;
  category?: { name: string };
}

const TABS = ["knowledge", "categories", "pipelines", "run"] as const;
type Tab = (typeof TABS)[number];

export function CollectionDetail() {
  const params = useParams<{ id: string }>();
  const collectionId = Number(params.id);

  const canEdit = usePermission("collection:edit");
  const canRun = usePermission("generation:run");

  const [tab, setTab] = useState<Tab>("knowledge");
  const [refreshKey, setRefreshKey] = useState(0);
  const { toast, element } = useToast();

  const collection = useApi<CollectionDetailData>(`/collections/${collectionId}`, [
    collectionId,
    refreshKey,
  ]);

  // Poll while anything is still uploading or indexing — indexing a real PDF
  // can take minutes, and the status must update without a manual refresh.
  useEffect(() => {
    const active = collection.data?.knowledgeSources.some(
      (s) => s.indexingStatus === "uploading" || s.indexingStatus === "indexing",
    );
    if (!active) return;
    const timer = setInterval(() => setRefreshKey((k) => k + 1), 3000);
    return () => clearInterval(timer);
  }, [collection.data]);

  if (collection.error) {
    return <Notice kind="error">Cannot load collection: {collection.error}</Notice>;
  }
  if (!collection.data) {
    return <FullScreenLoader label="Loading collection…" />;
  }

  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <>
      <div className="card">
        <div className="row between">
          <div>
            <h2>{collection.data.name}</h2>
            {collection.data.description && <div className="muted">{collection.data.description}</div>}
          </div>
          <span className={`badge ${collection.data.vectorStoreId ? "generated" : "pending"}`}>
            {collection.data.vectorStoreId ? "Indexed" : "No knowledge yet"}
          </span>
        </div>

        {canEdit && (
          <CollectionSettings collection={collection.data} onChange={refresh} toast={toast} />
        )}

        {/* Knowledge Sources is the one tab an uploader needs; the rest
            configure what gets generated and published. Hidden rather than
            disabled — a tab that cannot be opened is just a dead target. */}
        <div className="editor-tabs" style={{ marginTop: 12 }}>
          <button className={tab === "knowledge" ? "active" : ""} onClick={() => setTab("knowledge")}>
            Knowledge Sources ({collection.data.knowledgeSources.length})
          </button>
          {canEdit && (
            <button
              className={tab === "categories" ? "active" : ""}
              onClick={() => setTab("categories")}
            >
              Categories ({collection.data.categories.length})
            </button>
          )}
          {canEdit && (
            <button
              className={tab === "pipelines" ? "active" : ""}
              onClick={() => setTab("pipelines")}
            >
              Pipelines
            </button>
          )}
          {canRun && (
            <button className={tab === "run" ? "active" : ""} onClick={() => setTab("run")}>
              Run
            </button>
          )}
        </div>
      </div>

      {tab === "knowledge" && (
        <KnowledgeTab collection={collection.data} onChange={refresh} toast={toast} />
      )}
      {tab === "categories" && (
        <CategoriesTab collection={collection.data} onChange={refresh} toast={toast} />
      )}
      {tab === "pipelines" && (
        <PipelinesTab collectionId={collection.data.id} collectionModel={collection.data.model} toast={toast} />
      )}
      {tab === "run" && <RunTab collection={collection.data} toast={toast} />}

      {element}
    </>
  );
}

// ---------------------------------------------------------------------------
// Collection settings — model and general-knowledge default.
//
// These are always visible in the header, not tucked into a tab, because a
// collection's model silently overrides OPENAI_MODEL from .env for every
// generation against it (a request can only use one model, so *some* value
// has to win — the collection's, since it is the more specific setting).
// Without this control, changing .env looks like it has no effect: the
// collection created earlier is still holding whatever model it started
// with, and there was previously no way to see or change that short of a
// direct API call.
// ---------------------------------------------------------------------------

function CollectionSettings({
  collection,
  onChange,
  toast,
}: {
  collection: CollectionDetailData;
  onChange: () => void;
  toast: (msg: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(collection.name);
  const [description, setDescription] = useState(collection.description ?? "");
  const [model, setModel] = useState(collection.model);
  const [openaiKeyName, setOpenaiKeyName] = useState(collection.openaiKeyName ?? "");
  const [saving, setSaving] = useState(false);
  const models = useApi<string[]>("/models");
  const keyNames = useApi<{ names: string[] }>("/collections/openai-keys");

  function startEdit() {
    // Seeded on open rather than kept in sync: the form is a draft, and a
    // background refresh mid-edit must not overwrite what is being typed.
    setName(collection.name);
    setDescription(collection.description ?? "");
    setModel(collection.model);
    setOpenaiKeyName(collection.openaiKeyName ?? "");
    setEditing(true);
  }

  async function save() {
    if (!name.trim()) {
      toast("Name cannot be empty");
      return;
    }
    if (!model.trim()) {
      toast("Model cannot be empty");
      return;
    }
    setSaving(true);
    try {
      // Syllabus is deliberately not sent: re-pointing a collection would
      // leave every chapter scope on its PDFs referencing the old syllabus's
      // chapters. The update schema rejects it server-side too.
      await api.put(`/collections/${collection.id}`, {
        name: name.trim(),
        description: description.trim() || null,
        model: model.trim(),
        // Sent only when changed: the server refuses a change once the
        // collection holds files under the current key, and an unchanged key
        // should never trip that check.
        ...((collection.openaiKeyName ?? "") !== openaiKeyName
          ? { openaiKeyName: openaiKeyName || null }
          : {}),
      });
      toast("Collection updated");
      setEditing(false);
      onChange();
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const syllabus =
    collection.board && collection.className && collection.subject
      ? `${collection.board} · ${collection.className} · ${collection.subject}`
      : null;

  if (!editing) {
    return (
      <div className="row" style={{ marginTop: 10, gap: 16, flexWrap: "wrap" }}>
        <span className="mini muted">
          model: <code>{collection.model}</code>
        </span>
        <span className="mini muted">
          OpenAI key: <code>{collection.openaiKeyName ?? "default"}</code>
        </span>
        <span className="mini muted">
          syllabus: {syllabus ?? <span style={{ color: "var(--red)" }}>not mapped</span>}
        </span>
        <button className="btn mini" onClick={startEdit}>
          Edit
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      <div className="row" style={{ gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div className="field" style={{ flex: 2, minWidth: 200 }}>
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1, minWidth: 200 }}>
          <label>Model</label>
          <ModelSelect value={model} onChange={setModel} models={models.data ?? []} />
        </div>
        {((keyNames.data?.names.length ?? 0) > 0 || collection.openaiKeyName) && (
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label>OpenAI key</label>
            <select value={openaiKeyName} onChange={(e) => setOpenaiKeyName(e.target.value)}>
              <option value="">Default key</option>
              {(keyNames.data?.names ?? []).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <div className="mini muted">
              Cannot be changed once the collection has knowledge sources, documents or batches in flight.
            </div>
          </div>
        )}
      </div>

      <div className="field">
        <label>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={{ minHeight: 60 }}
          placeholder="Optional"
        />
      </div>

      <div className="field">
        <label>Syllabus</label>
        <div className="mini muted">
          {syllabus ?? "Not mapped"} — fixed after creation, because the knowledge sources below
          are mapped to this syllabus&apos;s chapters. Archive and recreate to change it.
        </div>
      </div>

      <div className="row" style={{ gap: 8 }}>
        <button className="btn primary mini" onClick={() => void save()} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button className="btn mini" onClick={() => setEditing(false)} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Knowledge Sources
// ---------------------------------------------------------------------------

/**
 * "Added by Asha on 12/03/2026, 14:02 · edited by Ravi …"
 *
 * The edit half is dropped when nothing has changed it, rather than repeating
 * the uploader twice — on a freshly uploaded source both columns hold the same
 * person, and showing that says nothing.
 *
 * "Unknown" covers sources predating migration 046 and users since deleted;
 * both are genuinely unattributable, and a blank would read as a bug.
 */
/**
 * Who added a source and who last changed it, sized for a table cell.
 *
 * The date is short and the full timestamp lives in `title`: a cell showing
 * "31/08/2026, 14:02:11" for both lines pushes the columns that matter off the
 * screen, and the exact second is rarely what anyone is after.
 *
 * The edit line is dropped when nothing has changed it — on a freshly uploaded
 * source both columns hold the same person, and saying so twice adds nothing.
 *
 * "Unknown" covers sources predating migration 046 and users since deleted;
 * both are genuinely unattributable, and a blank would read as a bug.
 */
function sourceAttribution(source: KnowledgeSourceRow): {
  added: string;
  edited: string | null;
  title: string;
} {
  const shortDate = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : null;
  const fullDate = (iso?: string) => (iso ? new Date(iso).toLocaleString() : null);

  const addedBy = source.createdByUser?.name ?? "Unknown";
  const addedOn = shortDate(source.createdAt);
  const added = addedOn ? `${addedBy} · ${addedOn}` : addedBy;

  const editedFull = fullDate(source.updatedAt);
  const addedFull = fullDate(source.createdAt);
  const changed = editedFull && editedFull !== addedFull;

  let edited: string | null = null;
  const titleParts = [addedFull ? `Added by ${addedBy} on ${addedFull}` : `Added by ${addedBy}`];
  if (changed) {
    const editedBy = source.updatedByUser?.name ?? "Unknown";
    edited = `edited ${editedBy} · ${shortDate(source.updatedAt)}`;
    titleParts.push(`edited by ${editedBy} on ${editedFull}`);
  }

  return { added, edited, title: titleParts.join(" · ") };
}

/** The three file roles, as a short label rather than the stored constant. */
function roleLabel(roleTag: string | undefined): string {
  if (roleTag === "BOOK_BACK_QUESTIONS") return "📖 Book-Back";
  if (roleTag === "CHAPTER_GUIDE") return "💡 Guide";
  return "📚 Textbook";
}

function roleBadgeClass(roleTag: string | undefined): string {
  if (roleTag === "BOOK_BACK_QUESTIONS") return "info";
  if (roleTag === "CHAPTER_GUIDE") return "approved";
  return "neutral";
}

function KnowledgeTab({
  collection,
  onChange,
  toast,
}: {
  collection: CollectionDetailData;
  onChange: () => void;
  toast: (msg: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // One syllabus section, doing double duty: it filters the list below, and
  // it is the chapter a new upload is mapped to. Sticky because mapping a
  // syllabus worth of chapter PDFs means otherwise re-picking the same
  // board/class/subject on every single upload.
  //
  // Keyed by collection id: this component stays mounted across a
  // collection switch (only the :id route param changes), so an unscoped key
  // carried one collection's chapter/subchapter/topic selection into the
  // next — filtering the new collection's list by an id from a different
  // syllabus, or worse, silently mapping the next upload to it. Scoping by
  // collection keeps the "sticky for weeks on one syllabus" behaviour the
  // comment above describes while still resetting when the collection itself
  // changes.
  const [chapterId, setChapterId] = useStickyState<string | null>(
    `zf.ks.${collection.id}.chapterId`,
    null,
  );
  const [subChapterId, setSubChapterId] = useStickyState<string | null>(
    `zf.ks.${collection.id}.subChapterId`,
    null,
  );
  const [topicId, setTopicId] = useStickyState<string | null>(
    `zf.ks.${collection.id}.topicId`,
    null,
  );

  // The syllabus comes from the collection itself (migration 020), not from
  // pickers here: a collection covers exactly one, so offering a choice could
  // only ever produce a mapping to chapters outside it.
  const { board, className, subject } = collection;
  const tree = useApi<ChapterTree>(
    board && className && subject
      ? `/chapter-tree?board=${encodeURIComponent(board)}&class=${encodeURIComponent(className)}&subject=${encodeURIComponent(subject)}`
      : null,
    [board, className, subject],
  );

  const chapters = tree.data?.chapters ?? [];
  const chapter = chapters.find((c) => c.id === chapterId);
  const subChapters = chapter?.subChapters ?? [];
  const topics = subChapters.find((sc) => sc.id === subChapterId)?.topics ?? [];

  // Names for the ids stored on each source, so the list shows a syllabus
  // rather than a UUID. Only resolvable for the syllabus currently loaded — a
  // PDF mapped to a different subject falls back to a generic label.
  const chapterNames = new Map(chapters.map((c) => [c.id, c.name]));
  const subChapterNames = new Map(
    chapters.flatMap((c) => c.subChapters.map((sc) => [sc.id, sc.name] as const)),
  );
  const categoryNames = new Map(collection.categories.map((c) => [c.id, c.name]));
  const topicNames = new Map(
    chapters.flatMap((c) =>
      c.subChapters.flatMap((sc) => sc.topics.map((t) => [t.id, t.name] as const)),
    ),
  );

  function scopeLabel(source: KnowledgeSourceRow): string {
    const scope = source.scopes?.[0];
    if (!scope) return "Whole collection";
    const chapterName = chapterNames.get(scope.chapterId);
    if (!chapterName) return "Mapped to another syllabus";
    const subName = scope.subChapterId ? subChapterNames.get(scope.subChapterId) : null;
    const topicName = scope.topicId ? topicNames.get(scope.topicId) : null;
    // Chapter level only (migration 058) — a subchapter/topic scope never
    // carries one, so this is unreachable when subName is set.
    const categoryName = scope.categoryId ? categoryNames.get(scope.categoryId) : null;
    const categorySuffix = categoryName ? ` (${categoryName} only)` : "";
    if (!subName) return `${chapterName}${categorySuffix}`;
    return topicName
      ? `${chapterName} > ${subName} > ${topicName}`
      : `${chapterName} > ${subName}`;
  }

  // Plain listing filters: each one is optional and matches exactly, nothing
  // more. Deliberately NOT modelling the generation fallback here — a
  // subchapter with no PDF of its own falling back to the chapter's is the
  // resolver's job (scope-service), and duplicating it in the list only made
  // the filters behave in ways the dropdowns did not describe.
  //
  // Not sticky across sessions like chapter/subChapter/topic above, so it
  // needs its own reset: this component stays mounted across a collection
  // switch (only the :id route param changes), and without this a role
  // filter picked for one collection silently hid sources in the next one
  // that happen to share the same role tag value.
  const [roleTagFilter, setRoleTagFilter] = useState<string>("ALL");
  useEffect(() => {
    setRoleTagFilter("ALL");
  }, [collection.id]);

  /* The Add PDF form's own state.
   *
   * Separate from the chapter/subchapter/topic dropdowns above the list on
   * purpose: those used to be both the list filter and the destination for the
   * next upload, so narrowing the list silently changed where the next file
   * would land, and filtering to see what a chapter already had was enough to
   * mis-map the next upload. The form now carries its own selection and the
   * dropdowns went back to filtering only. */
  /** null = closed, "add" = new upload, or the source being edited. Sharing
   *  one form is what keeps Add and Edit offering the same fields. */
  const [formMode, setFormMode] = useState<"add" | KnowledgeSourceRow | null>(null);
  const addOpen = formMode !== null;
  const editingSource = formMode === "add" ? null : formMode;
  const [addFile, setAddFile] = useState<File | null>(null);
  const [addChapterId, setAddChapterId] = useState<string | null>(null);
  const [addSubChapterId, setAddSubChapterId] = useState<string | null>(null);
  const [addTopicId, setAddTopicId] = useState<string | null>(null);
  /** Dedicates this source to one Book Back category at the chapter rung
   *  (migration 058) — null means "every ordinary category can read it",
   *  same as before this field existed. Only meaningful at chapter scope:
   *  cleared whenever a subchapter is picked, same as addTopicId is cleared
   *  without a subchapter. */
  const [addCategoryId, setAddCategoryId] = useState<number | null>(null);
  const [addRoleTag, setAddRoleTag] =
    useState<"TEXTBOOK" | "BOOK_BACK_QUESTIONS" | "CHAPTER_GUIDE">("TEXTBOOK");
  const [addModel, setAddModel] = useState<string>("");
  const [addEffort, setAddEffort] = useState<"" | "low" | "medium" | "high">("");
  // The "how it is read" choice was retired: every upload now attaches the
  // whole file rather than indexing it into the vector store, so this is
  // fixed rather than user-selectable. Kept as a constant rather than deleted
  // outright — handleUpload/handleSaveEdit and the API both still take
  // retrievalMode, and existing file_search sources are unaffected until
  // someone re-uploads or edits them.
  const addRetrievalMode = "direct_attach" as const;

  const models = useApi<string[]>("/models");

  const addChapter = chapters.find((c) => c.id === addChapterId);
  const addSubChapters = addChapter?.subChapters ?? [];
  const addTopics = addSubChapters.find((sc) => sc.id === addSubChapterId)?.topics ?? [];
  // Only Book Back categories are offered — that's the only category type
  // whose resolution actually reads this field (see resolveScope's
  // bookBackCategoryId). Offering a MODULE/BOOK_IN category would look
  // configurable but do nothing.
  const bookBackCategories = collection.categories.filter((c) => getCategoryType(c) === "BOOK_BACK");

  function resetAddForm() {
    setAddFile(null);
    setAddChapterId(null);
    setAddSubChapterId(null);
    setAddTopicId(null);
    setAddCategoryId(null);
    setAddRoleTag("TEXTBOOK");
    setAddModel("");
    setAddEffort("");
    if (fileInput.current) fileInput.current.value = "";
  }

  // Closes and clears the Add/Edit form on a collection switch. Left open, it
  // would still submit to the right collection (the upload URL reads
  // collection.id fresh) but with a chapter/subChapter/topic selection carried
  // over from the collection just left — silently mis-mapping the upload
  // within the new collection's syllabus.
  useEffect(() => {
    setFormMode(null);
    resetAddForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection.id]);

  /**
   * Opens the form seeded from whatever the list is filtered to.
   *
   * Someone narrowing the list to a chapter is usually about to add a PDF for
   * that chapter, so pre-filling saves re-picking it — while leaving every
   * field editable, which is the part the old behaviour got wrong: the filters
   * *were* the destination, with no way to upload anywhere else without first
   * changing what the list showed.
   *
   * Seeded here rather than in the state initialisers, which run once at mount
   * and would hold whatever the filters were then.
   */
  function openAddForm() {
    setAddFile(null);
    setAddChapterId(chapterId);
    setAddSubChapterId(subChapterId);
    setAddTopicId(topicId);
    // A role filter of "ALL" is not a choice about the new file, so it falls
    // back to the usual default.
    setAddRoleTag(
      roleTagFilter === "TEXTBOOK" ||
        roleTagFilter === "BOOK_BACK_QUESTIONS" ||
        roleTagFilter === "CHAPTER_GUIDE"
        ? roleTagFilter
        : "TEXTBOOK",
    );
    setAddModel("");
    setAddEffort("");
    if (fileInput.current) fileInput.current.value = "";
    setFormMode("add");
  }

  /**
   * Opens the same form on an existing PDF, seeded from what that PDF already
   * has rather than from the filters.
   *
   * Editing used to be a pair of buttons that mapped the row to whatever the
   * filter dropdowns happened to show — so changing a PDF's chapter meant
   * first re-filtering the list to the destination, and role, model and
   * complexity could not be changed at all.
   */
  function openEditForm(source: KnowledgeSourceRow) {
    const scope = source.scopes?.[0];
    setAddFile(null);
    setAddChapterId(scope?.chapterId ?? null);
    setAddSubChapterId(scope?.subChapterId ?? null);
    setAddTopicId(scope?.topicId ?? null);
    setAddCategoryId(scope?.categoryId ?? null);
    setAddRoleTag(
      source.roleTag === "BOOK_BACK_QUESTIONS" || source.roleTag === "CHAPTER_GUIDE"
        ? source.roleTag
        : "TEXTBOOK",
    );
    setAddModel(source.defaultModel ?? "");
    setAddEffort(source.defaultReasoningEffort ?? "");
    if (fileInput.current) fileInput.current.value = "";
    setFormMode(source);
  }

  /** Saves the form's fields onto an existing source: scope, role, model and
   *  complexity in one request. */
  async function handleSaveEdit(source: KnowledgeSourceRow) {
    setUploading(true);
    try {
      await api.put(`/collections/${collection.id}/knowledge-sources/${source.id}/scope`, {
        scope: addChapterId
          ? {
              chapterId: addChapterId,
              subChapterId: addSubChapterId,
              topicId: addTopicId,
              // Chapter level only — dropped server-side (setScope) whenever
              // a subchapter is set, same as topicId without one.
              categoryId: addSubChapterId ? null : addCategoryId,
            }
          : null,
        roleTag: addRoleTag,
        model: addModel || null,
        reasoningEffort: addEffort || null,
        retrievalMode: addRetrievalMode,
      });
      if (addChapterId) {
        // Names where it went. Saving can move the row out of the current
        // filter, and a bare "updated" then looks like the PDF vanishing.
        const where = [
          chapterNames.get(addChapterId),
          addSubChapterId ? subChapterNames.get(addSubChapterId) : null,
          addTopicId ? topicNames.get(addTopicId) : null,
        ]
          .filter(Boolean)
          .join(" > ");
        toast(where ? `${source.filename} saved — mapped to ${where}` : `${source.filename} saved`);
      } else {
        toast(`${source.filename} saved — now unmapped`);
      }
      setFormMode(null);
      resetAddForm();
      onChange();
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Failed to save changes");
    } finally {
      setUploading(false);
    }
  }
  const [fileDeleteModal, setFileDeleteModal] = useState<FileDeleteModalState | null>(null);
  /** The source currently being previewed in the PDF viewer modal, or null when closed. */
  const [viewingSource, setViewingSource] = useState<KnowledgeSourceRow | null>(null);


  function matchesFilter(source: KnowledgeSourceRow): boolean {
    const scope = source.scopes?.[0];
    if (chapterId && scope?.chapterId !== chapterId) return false;
    if (subChapterId && scope?.subChapterId !== subChapterId) return false;
    if (topicId && scope?.topicId !== topicId) return false;
    if (roleTagFilter !== "ALL" && (source.roleTag ?? "TEXTBOOK") !== roleTagFilter) return false;
    return true;
  }

  // A plain filter now. Editing happens in a modal with its own chapter
  // pickers, so changing these dropdowns can no longer pull the destination
  // out from under an edit in progress — the reason this used to force the
  // edited row to stay visible.
  const visibleSources = collection.knowledgeSources.filter(matchesFilter);

  async function handleUpload() {
    if (!addFile) {
      toast("Choose a PDF first");
      return;
    }
    if (addFile.type !== "application/pdf") {
      toast("Only PDF files are accepted");
      return;
    }
    setUploading(true);
    try {
      await uploadFile(`/collections/${collection.id}/knowledge-sources`, addFile, {
        chapterId: addChapterId,
        subChapterId: addSubChapterId,
        topicId: addTopicId,
        // Chapter level only — dropped server-side whenever a subchapter is
        // set, same as topicId without one.
        categoryId: addSubChapterId ? null : addCategoryId != null ? String(addCategoryId) : null,
        roleTag: addRoleTag,
        retrievalMode: addRetrievalMode,
        // Recorded against the source; nothing reads them yet.
        model: addModel || null,
        reasoningEffort: addEffort || null,
      });
      toast(
        `${addFile.name} uploaded${addChapterId ? " and mapped" : ""} — attached whole, not indexed`,
      );
      setFormMode(null);
      resetAddForm();
      onChange();
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function remove(sourceId: number, filename: string) {
    setFileDeleteModal({
      title: "Remove Knowledge Source File",
      filename,
      onConfirm: async (purgeRemote: boolean) => {
        try {
          await api.delete(
            `/collections/${collection.id}/knowledge-sources/${sourceId}?purgeRemote=${purgeRemote}`,
          );
          toast(`${filename} removed ${purgeRemote ? "(from DB & Vector Store)" : "(from DB only)"}`);
          onChange();
        } catch (error: unknown) {
          toast(error instanceof Error ? error.message : "Remove failed");
        }
      },
    });
  }

  return (
    <div className="card">
      <div className="row between">
        <div>
          <h2 style={{ margin: 0 }}>Knowledge sources</h2>
          {/* The syllabus is fixed by the collection, so it is shown rather
              than offered as a choice. */}
          <div className="mini muted">
            {board && className && subject
              ? `${board} · ${className} · ${subject}`
              : "No syllabus mapped to this collection"}
          </div>
        </div>
        <div>
          <button
            className="btn"
            style={{ marginRight: 8 }}
            disabled={!chapterId && !subChapterId && !topicId && roleTagFilter === "ALL"}
            title="Clears the list filter"
            onClick={() => {
              setChapterId(null);
              setSubChapterId(null);
              setTopicId(null);
              setRoleTagFilter("ALL");
            }}
          >
            Clear filter
          </button>
          <button className="btn primary" onClick={openAddForm}>
            + Add PDF
          </button>
        </div>
      </div>

      <div className="row" style={{ gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div className="field" style={{ flex: 1.4, minWidth: 160 }}>
          <label>Chapter</label>
          <select
            value={chapterId ?? ""}
            disabled={chapters.length === 0}
            onChange={(e) => {
              setChapterId(e.target.value || null);
              setSubChapterId(null);
              setTopicId(null);
            }}
          >
            <option value="">All chapters</option>
            {chapters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.sequenceNo !== null ? `${c.sequenceNo}. ` : ""}
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: 1.4, minWidth: 160 }}>
          <label>Subchapter</label>
          <select
            value={subChapterId ?? ""}
            disabled={!chapterId || subChapters.length === 0}
            onChange={(e) => {
              setSubChapterId(e.target.value || null);
              setTopicId(null);
            }}
          >
            <option value="">Whole chapter</option>
            {subChapters.map((sc) => (
              <option key={sc.id} value={sc.id}>
                {sc.sequenceNo !== null ? `${sc.sequenceNo}. ` : ""}
                {sc.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: 1.4, minWidth: 160 }}>
          <label>Topic</label>
          <select
            value={topicId ?? ""}
            disabled={!subChapterId || topics.length === 0}
            onChange={(e) => setTopicId(e.target.value || null)}
          >
            <option value="">Whole subchapter</option>
            {topics.map((t) => (
              <option key={t.id} value={t.id}>
                {t.sequenceNo !== null ? `${t.sequenceNo}. ` : ""}
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: 1, minWidth: 140 }}>
          <label>Filter by Role</label>
          <select value={roleTagFilter} onChange={(e) => setRoleTagFilter(e.target.value)}>
            <option value="ALL">All File Roles</option>
            <option value="TEXTBOOK">Textbook PDFs</option>
            <option value="BOOK_BACK_QUESTIONS">Book-Back Questions</option>
            <option value="CHAPTER_GUIDE">Chapter Guides</option>
          </select>
        </div>
      </div>

      <p className="mini muted">
        {chapterId
          ? `Showing PDFs mapped to ${
              topicId ? "this topic" : subChapterId ? "this subchapter" : "this chapter"
            }.`
          : "Showing every PDF. These filter the list only — where a new PDF is mapped is chosen on the Add PDF form."}
      </p>

      {collection.knowledgeSources.length === 0 && (
        <p className="muted">
          No knowledge sources yet. Without one, categories can only run with general knowledge
          allowed - otherwise every generation reports the topic as not covered.
        </p>
      )}

      {collection.knowledgeSources.length > 0 && visibleSources.length === 0 && (
        <p className="muted">
          No PDFs mapped to{" "}
          {topicId ? "this topic" : subChapterId ? "this subchapter" : "this chapter"} yet.
          {topicId
            ? " Generation still falls back to the subchapter's PDF, then the chapter's."
            : subChapterId
              ? " Generation still falls back to the chapter's PDF if it has one."
              : ""}
        </p>
      )}

      {visibleSources.length > 0 && (
        /* A table rather than stacked cards: every source carries the same
           eight facts, and the point of this screen is comparing them across
           rows — which PDF covers which chapter, which is still indexing,
           which has no mapping. Cards forced that comparison to happen by
           reading paragraphs, and hid model and complexity entirely. */
        <div className="ks-table-wrap">
          <table className="ks-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Role</th>
                <th>Mapped to</th>
                <th>Model</th>
                <th>Complexity</th>
                <th className="ks-num">Size</th>
                <th>Status</th>
                <th>Added / edited</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleSources.map((source) => {
                const unmapped = (source.scopes?.length ?? 0) === 0;
                const actor = sourceAttribution(source);
                return (
                  <tr key={source.id} className={unmapped ? "ks-unmapped" : undefined}>
                    <td>
                      <span className="ks-filename" title={source.filename}>
                        {source.filename}
                      </span>
                      {source.errorMessage && (
                        <div className="mini" style={{ color: "var(--red)" }}>
                          {source.errorMessage}
                        </div>
                      )}
                    </td>

                    <td>
                      {source.retrievalMode === "direct_attach" ? (
                        // Worth surfacing: an attached source is never
                        // searched, so "why did retrieval find nothing" has a
                        // visible answer on the row itself.
                        <span className="badge mini" title="Attached whole to each request; not indexed">
                          📎 Attached
                        </span>
                      ) : null}
                      <span className={`badge mini ${roleBadgeClass(source.roleTag)}`}>
                        {roleLabel(source.roleTag)}
                      </span>
                    </td>

                    {/* Collection-wide is worth pointing at: it means every
                        generation in the collection can reach this PDF, which
                        is rarely what someone intended. */}
                    <td className={unmapped ? "ks-warn" : undefined}>{scopeLabel(source)}</td>

                    <td>
                      {source.defaultModel ? (
                        <code className="ks-code">{source.defaultModel}</code>
                      ) : (
                        <span className="muted mini">Collection default</span>
                      )}
                    </td>

                    <td>
                      {source.defaultReasoningEffort ? (
                        <span className={`ks-effort is-${source.defaultReasoningEffort}`}>
                          {source.defaultReasoningEffort}
                        </span>
                      ) : (
                        <span className="muted mini">—</span>
                      )}
                    </td>

                    <td className="ks-num">
                      {source.sizeBytes ? `${(source.sizeBytes / 1024 / 1024).toFixed(1)} MB` : "—"}
                    </td>

                    <td>
                      <span
                        className={`badge mini ${
                          source.indexingStatus === "ready"
                            ? "generated"
                            : source.indexingStatus === "failed"
                              ? "failed"
                              : "processing"
                        }`}
                      >
                        {source.indexingStatus}
                      </span>
                    </td>

                    <td className="ks-actor" title={actor.title}>
                      <div>{actor.added}</div>
                      {actor.edited && <div className="muted">{actor.edited}</div>}
                    </td>

                    <td className="ks-actions">
                      <button
                        className="btn mini"
                        title={
                          source.indexingStatus !== "ready"
                            ? "PDF is not ready yet — wait for indexing to complete"
                            : "Preview the PDF"
                        }
                        disabled={source.indexingStatus !== "ready"}
                        onClick={() => setViewingSource(source)}
                      >
                        View
                      </button>
                      <button className="btn mini" onClick={() => openEditForm(source)}>
                        Edit
                      </button>
                      <button
                        className="btn mini"
                        onClick={() => void remove(source.id, source.filename)}
                      >
                        Remove
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {addOpen && (
        <div
          className="modal-backdrop"
          onClick={() => {
            if (!uploading) {
              setFormMode(null);
              resetAddForm();
            }
          }}
        >
          <div className="modal-card wide" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{editingSource ? "Edit PDF" : "Add PDF"}</h3>
            <div className="mini muted" style={{ marginBottom: 12 }}>
              {board && className && subject
                ? `${board} · ${className} · ${subject}`
                : "No syllabus mapped to this collection"}
              {editingSource
                ? ` · ${editingSource.filename}`
                : chapterId && " · prefilled from the current filter — change anything below"}
            </div>

            {/* No file picker when editing: the PDF itself is already indexed
                into the vector store, and swapping it would be a re-upload
                rather than an edit. Everything else is editable. */}
            {!editingSource && (
              <div className="field">
                <label>PDF file</label>
                <input
                  ref={fileInput}
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setAddFile(e.target.files?.[0] ?? null)}
                />
                {addFile && (
                  <span className="mini muted">
                    {addFile.name} · {(addFile.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                )}
              </div>
            )}

            <div className="row" style={{ gap: 12, alignItems: "flex-start" }}>
              <div className="field" style={{ flex: 1, minWidth: 150 }}>
                <label>Chapter</label>
                <select
                  value={addChapterId ?? ""}
                  disabled={chapters.length === 0}
                  onChange={(e) => {
                    setAddChapterId(e.target.value || null);
                    setAddSubChapterId(null);
                    setAddTopicId(null);
                    setAddCategoryId(null);
                  }}
                >
                  <option value="">Whole collection</option>
                  {chapters.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.sequenceNo !== null ? `${c.sequenceNo}. ` : ""}
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: 1, minWidth: 150 }}>
                <label>Subchapter</label>
                <select
                  value={addSubChapterId ?? ""}
                  disabled={!addChapterId || addSubChapters.length === 0}
                  onChange={(e) => {
                    setAddSubChapterId(e.target.value || null);
                    setAddTopicId(null);
                    setAddCategoryId(null);
                  }}
                >
                  <option value="">Whole chapter</option>
                  {addSubChapters.map((sc) => (
                    <option key={sc.id} value={sc.id}>
                      {sc.sequenceNo !== null ? `${sc.sequenceNo}. ` : ""}
                      {sc.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: 1, minWidth: 150 }}>
                <label>Topic</label>
                <select
                  value={addTopicId ?? ""}
                  disabled={!addSubChapterId || addTopics.length === 0}
                  onChange={(e) => setAddTopicId(e.target.value || null)}
                >
                  <option value="">Whole subchapter</option>
                  {addTopics.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.sequenceNo !== null ? `${t.sequenceNo}. ` : ""}
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Chapter level only — the resolver ignores this field for a
                subchapter/topic scope, so offering it there would look
                configurable and do nothing. Also hidden entirely when the
                collection has no Book Back categories, since no other
                category type reads it either. */}
            {addChapterId && !addSubChapterId && bookBackCategories.length > 0 && (
              <div className="field">
                <label>Book Back category</label>
                <select
                  value={addCategoryId ?? ""}
                  onChange={(e) => setAddCategoryId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Every Book Back category (shared, current behaviour)</option>
                  {bookBackCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <small className="muted">
                  Dedicate this PDF to one Book Back category — e.g. an MCQ paper only
                  feeding the MCQ category, not Fill-ups or Match. Leave unset to keep
                  today&apos;s behaviour, where every Book Back category at this chapter
                  reads every unassigned source.
                </small>
              </div>
            )}

            <div className="row" style={{ gap: 12, alignItems: "flex-start" }}>
              <div className="field" style={{ flex: 1, minWidth: 150 }}>
                <label>File role</label>
                <select
                  value={addRoleTag}
                  onChange={(e) =>
                    setAddRoleTag(
                      e.target.value as "TEXTBOOK" | "BOOK_BACK_QUESTIONS" | "CHAPTER_GUIDE",
                    )
                  }
                >
                  <option value="TEXTBOOK">Textbook PDF</option>
                  <option value="BOOK_BACK_QUESTIONS">Book-Back Questions PDF</option>
                  <option value="CHAPTER_GUIDE">Chapter Guide PDF</option>
                </select>
              </div>
              <div className="field" style={{ flex: 1, minWidth: 150 }}>
                <label>Model</label>
                <select value={addModel} onChange={(e) => setAddModel(e.target.value)}>
                  <option value="">Collection default ({collection.model})</option>
                  {models.data?.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: 1, minWidth: 150 }}>
                <label>Complexity</label>
                <select
                  value={addEffort}
                  onChange={(e) => setAddEffort(e.target.value as "" | "low" | "medium" | "high")}
                >
                  <option value="">Not specified</option>
                  <option value="low">Low - straightforward material</option>
                  <option value="medium">Medium</option>
                  <option value="high">High - dense or technical</option>
                </select>
              </div>
            </div>

            {/* Said plainly rather than left to be discovered: these are stored
                against the source but no generation path reads them yet. */}
            <p className="mini muted" style={{ marginTop: 4 }}>
              Model and complexity are saved with the PDF for later use. They do not affect
              generation yet.
            </p>

            <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="btn"
                disabled={uploading}
                onClick={() => {
                  setFormMode(null);
                  resetAddForm();
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={(!editingSource && !addFile) || uploading}
                onClick={() =>
                  editingSource ? void handleSaveEdit(editingSource) : void handleUpload()
                }
              >
                {uploading
                  ? editingSource
                    ? "Saving…"
                    : "Uploading & indexing..."
                  : editingSource
                    ? "Save changes"
                    : "Upload"}
              </button>
            </div>

            {uploading && !editingSource && (
              <p className="mini muted" style={{ marginTop: 8 }}>
                Indexing runs before this returns - a large PDF can take a few minutes.
              </p>
            )}
          </div>
        </div>
      )}

      {fileDeleteModal && (
        <FileRemovalModal modal={fileDeleteModal} onClose={() => setFileDeleteModal(null)} />
      )}

      {viewingSource && (
        <PdfViewerModal
          source={{
            id: viewingSource.id,
            collectionId: collection.id,
            filename: viewingSource.filename,
            roleTag: viewingSource.roleTag,
            scopeLabel: scopeLabel(viewingSource),
          }}
          onClose={() => setViewingSource(null)}
        />
      )}
    </div>
  );
}



// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export function getCategoryType(cat: {
  categoryType?: string;
  questionCategoryId?: string | null;
  questionAssessmentType?: string | null;
  code?: string;
  name?: string;
}): "MODULE" | "BOOK_IN" | "BOOK_BACK" {
  if (cat.categoryType === "MODULE" || cat.categoryType === "BOOK_IN" || cat.categoryType === "BOOK_BACK") {
    return cat.categoryType;
  }
  // Only for rows predating category_type (migration 031). Deliberately does
  // NOT test questionCategoryId: that field means "group these questions under
  // this existing book-back category", which a Book In category sets just as
  // legitimately — reading it as a type marker is what made a Book In category
  // appear in the Book-Back list.
  //
  // The name and code are weak evidence, but they are all a legacy row has,
  // and they are only consulted when the stored type is absent.
  if (cat.code?.toLowerCase().includes("book_back") || cat.name?.toLowerCase().includes("book-back")) {
    return "BOOK_BACK";
  }
  if (cat.questionAssessmentType) {
    return "BOOK_IN";
  }
  return "MODULE";
}

const EMPTY_CATEGORY_FORM = {
  name: "",
  code: "",
  categoryType: "MODULE" as "MODULE" | "BOOK_IN" | "BOOK_BACK",
  prompt: "",
  responseType: "html" as ResponseType,
  outputSchemaText: "",
  sampleOutput: "",
  sampleMode: "none" as SampleMode,
  isActive: true,
  externalModuleId: null as string | null,
  critiqueEnabled: false,
  critiquePrompt: "",
  critiqueResponseType: "text" as "text" | "json",
  critiqueOutputSchemaText: "",
  publishesQuestions: false,
  questionAssessmentType: "mcq",
  questionMarkText: "1",
  questionImportanceText: "3",
  questionCategoryId: "",
};

function CategoriesTab({
  collection,
  onChange,
  toast,
}: {
  collection: CollectionDetailData;
  onChange: () => void;
  toast: (msg: string) => void;
}) {
  const [categorySubTab, setCategorySubTab] = useState<"MODULE" | "BOOK_IN" | "BOOK_BACK">("MODULE");
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(EMPTY_CATEGORY_FORM);
  const [saving, setSaving] = useState(false);
  const [docBusy, setDocBusy] = useState(false);
  const [fileDeleteModal, setFileDeleteModal] = useState<FileDeleteModalState | null>(null);
  const { isPending, run } = usePendingActions();
  const [viewingDocx, setViewingDocx] = useState<{ filename: string; fileUrl: string; subtitle?: string } | null>(null);
  const promptDocInput = useRef<HTMLInputElement>(null);
  const modules = useApi<MainAppModule[]>("/modules");

  /** The main app's book-back categories for this collection's syllabus.
   *
   *  Fetched only while the form is open on a question category — the picker
   *  is the only thing that uses them, and every other category open would
   *  otherwise spend a main-app request against the shared 300-req/5min limit
   *  for a list it never shows. */
  const questionCategories = useApi<QuestionCategoryOption[]>(
    editingId !== null && form.publishesQuestions
      ? `/collections/${collection.id}/question-categories`
      : null,
    [editingId, form.publishesQuestions],
  );

  /** Only the categories matching the chosen question type. The main app keys
   *  book_back_category by assessmentType, so offering an "mcq" category to a
   *  match_the_following question would file it under a group its own question
   *  type does not belong to. */
  const matchingQuestionCategories = (questionCategories.data ?? []).filter(
    (category) => category.assessmentType === form.questionAssessmentType,
  );

  /** The attached document for the category being edited, or null when it has
   *  none. Read from the server record rather than the draft form, since
   *  uploading is immediate rather than part of Save.
   *
   *  Guarded on promptDocFileId, not on the category existing: the category
   *  row is always present while editing, so testing it alone rendered the
   *  "attached" state — Remove button and all — for a category with no
   *  document. */
  const editingCategory =
    typeof editingId === "number"
      ? (collection.categories.find((c) => c.id === editingId) ?? null)
      : null;
  const promptDoc = editingCategory?.promptDocFileId ? editingCategory : null;

  async function uploadPromptDoc(categoryId: number, file: File) {
    setDocBusy(true);
    try {
      await uploadFile(
        `/collections/${collection.id}/categories/${categoryId}/prompt-doc`,
        file,
      );
      toast(`${file.name} attached to this category`);
      onChange();
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setDocBusy(false);
      if (promptDocInput.current) promptDocInput.current.value = "";
    }
  }

  function removePromptDoc(categoryId: number, filename?: string | null) {
    setFileDeleteModal({
      title: "Remove Category Prompt Document",
      filename: filename ?? "Prompt Document",
      onConfirm: async (purgeRemote: boolean) => {
        setDocBusy(true);
        try {
          await api.delete(
            `/collections/${collection.id}/categories/${categoryId}/prompt-doc?purgeRemote=${purgeRemote}`,
          );
          toast(`Prompt document removed ${purgeRemote ? "(from DB & OpenAI)" : "(from DB only)"}`);
          onChange();
        } catch (error: unknown) {
          toast(error instanceof Error ? error.message : "Failed to remove");
        } finally {
          setDocBusy(false);
        }
      },
    });
  }

  function startNew() {
    // Seeded from the tab, which is the only place the intent actually
    // exists. Guessing it from the form's contents instead is what put
    // "Book In MCQ" in the Book-Back list: a Book In category legitimately
    // sets questionCategoryId to group its questions under an existing
    // book-back category, and that was being read as "this IS book-back".
    setForm({ ...EMPTY_CATEGORY_FORM, categoryType: categorySubTab });
    setEditingId("new");
  }

  function startEdit(category: Category) {
    setForm({
      name: category.name,
      code: category.code,
      categoryType: getCategoryType(category),
      prompt: category.prompt ?? "",
      responseType: category.responseType,
      outputSchemaText: category.outputSchema ? JSON.stringify(category.outputSchema, null, 2) : "",
      sampleOutput: category.sampleOutput ?? "",
      sampleMode: category.sampleMode,
      isActive: category.isActive,
      externalModuleId: category.externalModuleId,
      critiqueEnabled: category.critiqueEnabled,
      critiquePrompt: category.critiquePrompt ?? "",
      critiqueResponseType: category.critiqueResponseType,
      critiqueOutputSchemaText: category.critiqueOutputSchema
        ? JSON.stringify(category.critiqueOutputSchema, null, 2)
        : "",
      // The stored type IS the flag — a category with one set is a question
      // category, which is what the toggle reflects when reopening it.
      publishesQuestions: Boolean(category.questionAssessmentType),
      questionAssessmentType: category.questionAssessmentType ?? "mcq",
      questionMarkText: category.questionMark === null ? "1" : String(category.questionMark),
      questionImportanceText:
        category.questionImportance === null ? "3" : String(category.questionImportance),
      questionCategoryId: category.questionCategoryId ?? "",
    });
    setEditingId(category.id);
  }

  async function save() {
    const isQuestionCategory = form.publishesQuestions;

    let outputSchema: Record<string, unknown> | unknown[] | null = null;
    if (form.responseType === "json" && form.outputSchemaText.trim()) {
      try {
        outputSchema = JSON.parse(form.outputSchemaText) as Record<string, unknown> | unknown[];
      } catch {
        toast("Output schema is not valid JSON");
        return;
      }
    }

    let critiqueOutputSchema: Record<string, unknown> | unknown[] | null = null;
    if (form.critiqueEnabled && form.critiqueResponseType === "json" && form.critiqueOutputSchemaText.trim()) {
      try {
        critiqueOutputSchema = JSON.parse(form.critiqueOutputSchemaText) as Record<string, unknown> | unknown[];
      } catch {
        toast("Critique output schema is not valid JSON");
        return;
      }
    }

    // The form's own value, not a guess from its contents. A category that
    // publishes questions keeps whichever question type it was created under;
    // one that does not is a MODULE regardless of what the field says, since
    // the question settings are cleared on save anyway.
    const computedCategoryType = isQuestionCategory
      ? form.categoryType === "BOOK_BACK"
        ? "BOOK_BACK"
        : "BOOK_IN"
      : "MODULE";

    const payload = {
      name: form.name.trim(),
      code: form.code.trim(),
      categoryType: computedCategoryType,
      prompt: form.prompt.trim() || null,
      responseType: form.responseType,
      outputSchema,
      sampleOutput: form.sampleOutput.trim() || null,
      sampleMode: form.sampleOutput.trim() ? form.sampleMode : "none",
      isActive: form.isActive,
      externalModuleId: form.externalModuleId,
      critiqueEnabled: form.critiqueEnabled,
      critiquePrompt: form.critiqueEnabled ? form.critiquePrompt.trim() || null : null,
      critiqueResponseType: form.critiqueResponseType,
      critiqueOutputSchema,
      // Only sent for a question category. Null everywhere else, so an
      // ordinary category never carries stale question settings from a code
      // that was changed after the fact.
      questionAssessmentType: isQuestionCategory ? form.questionAssessmentType.trim() : null,
      questionMark: isQuestionCategory ? Number(form.questionMarkText) : null,
      questionImportance: isQuestionCategory ? Number(form.questionImportanceText) : null,
      questionCategoryId: isQuestionCategory ? form.questionCategoryId.trim() || null : null,
    };

    if (!payload.name || !payload.code) {
      toast("Name and code are required");
      return;
    }

    if (form.critiqueEnabled && !payload.critiquePrompt) {
      toast("Critique is enabled but no critique prompt was entered");
      return;
    }

    if (isQuestionCategory) {
      // The main app's bulk endpoint requires both on every call, so a
      // category missing either cannot publish. Caught here so the message
      // lands next to the field rather than after a generation has run.
      if (!payload.questionAssessmentType) {
        toast("Question type is required for a Book In Questions category");
        return;
      }
      if (!Number.isFinite(payload.questionMark) || (payload.questionMark ?? 0) < 1) {
        toast("Mark must be a positive whole number");
        return;
      }
    }

    setSaving(true);
    try {
      if (editingId === "new") {
        await api.post(`/collections/${collection.id}/categories`, payload);
      } else {
        await api.put(`/collections/${collection.id}/categories/${editingId}`, payload);
      }
      onChange();
      setEditingId(null);
      toast(editingId === "new" ? "Category created" : "Category saved");
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  /**
   * Copies a category's configuration into this collection.
   *
   * The case this exists for: five Book In Questions categories that differ
   * only in question type, mark and one section of prompt text. Rebuilding
   * each by hand means re-pasting a long shared preamble, and every re-paste
   * is a chance for them to drift.
   *
   * The copy opens for editing immediately — a clone is never the finished
   * article, and the next action is always to change something.
   */
  async function clone(category: Category) {
    try {
      const created = await run(`category-${category.id}-clone`, () =>
        api.post<Category>(`/collections/${collection.id}/categories/${category.id}/clone`, {}),
      );
      toast(`Cloned as "${created.name}" — edit it below`);
      onChange();
      startEdit(created);
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Clone failed");
    }
  }

  async function remove(category: Category) {
    if (!confirm(`Delete "${category.name}"? This cannot be undone.`)) return;
    try {
      await run(`category-${category.id}-delete`, () =>
        api.delete(`/collections/${collection.id}/categories/${category.id}`),
      );
      toast(`${category.name} deleted`);
      if (editingId === category.id) setEditingId(null);
      onChange();
    } catch (error: unknown) {
      // The service refuses to delete a category with jobs or content against
      // it, since category_id cascades on delete — that would silently
      // destroy reviewed and approved work, not just the category row.
      toast(error instanceof Error ? error.message : "Delete failed");
    }
  }

  return (
    <div className="grid">
      <div className="card span-7">
        <div className="row between">
          <h2>Categories</h2>
          <button className="btn mini" onClick={startNew}>
            + Add category
          </button>
        </div>

        <div className="row" style={{ gap: 8, marginTop: 12, marginBottom: 16 }}>
          <button
            type="button"
            className={`btn mini ${categorySubTab === "MODULE" ? "primary" : ""}`}
            onClick={() => setCategorySubTab("MODULE")}
          >
            📚 Module ({collection.categories.filter((c) => getCategoryType(c) === "MODULE").length})
          </button>
          <button
            type="button"
            className={`btn mini ${categorySubTab === "BOOK_IN" ? "primary" : ""}`}
            onClick={() => setCategorySubTab("BOOK_IN")}
          >
            ❓ Book-In ({collection.categories.filter((c) => getCategoryType(c) === "BOOK_IN").length})
          </button>
          <button
            type="button"
            className={`btn mini ${categorySubTab === "BOOK_BACK" ? "primary" : ""}`}
            onClick={() => setCategorySubTab("BOOK_BACK")}
          >
            📖 Book-Back ({collection.categories.filter((c) => getCategoryType(c) === "BOOK_BACK").length})
          </button>
        </div>

        {collection.categories.length === 0 && <p className="muted">No categories yet.</p>}
        {collection.categories.length > 0 &&
          collection.categories.filter((c) => getCategoryType(c) === categorySubTab).length === 0 && (
            <p className="muted">No {categorySubTab.toLowerCase().replace("_", " ")} categories in this tab yet.</p>
          )}

        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Type</th>
              <th>Sample</th>
              <th>Module</th>
              <th>Critique</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {collection.categories
              .filter((category) => getCategoryType(category) === categorySubTab)
              .map((category) => (
              <tr key={category.id}>
                <td>
                  <b>{category.name}</b>
                </td>
                <td>
                  <code>{category.code}</code>
                </td>
                <td>{category.responseType}</td>
                <td className="mini muted">{category.sampleMode === "none" ? "—" : category.sampleMode}</td>
                <td className="mini muted">
                  {/* A question category has no module to map to — it writes
                      to the question bank. Showing "not mapped" for it would
                      read as an unfinished setup rather than a different
                      destination. */}
                  {category.questionAssessmentType
                    ? `Question bank · ${category.questionAssessmentType}`
                    : category.externalModuleId
                      ? (modules.data?.find((m) => m.id === category.externalModuleId)?.name ??
                        "unmapped")
                      : "— not mapped"}
                </td>
                <td className="mini muted">{category.critiqueEnabled ? "On" : "—"}</td>
                <td>
                  <span className={`badge ${category.isActive ? "generated" : "pending"}`}>
                    {category.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <button className="btn mini" onClick={() => startEdit(category)}>
                    Edit
                  </button>
                  <ActionButton
                    className="mini"
                    pending={isPending(`category-${category.id}-clone`)}
                    onClick={() => void clone(category)}
                  >
                    Clone
                  </ActionButton>
                  <ActionButton
                    className="mini"
                    pending={isPending(`category-${category.id}-delete`)}
                    onClick={() => void remove(category)}
                  >
                    Delete
                  </ActionButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingId !== null && (
        /* Full width, not span-5. The editor is two columns — instructions
           beside destination settings — because stacking a 120px prompt
           textarea above everything else pushed the fields that decide where
           content publishes below the fold. */
        <div className="card span-12">
          <h2>{editingId === "new" ? "New category" : "Edit category"}</h2>

          <div className="row" style={{ gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div className="field" style={{ flex: "1 1 240px", minWidth: 200 }}>
              <label>Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>

            <div className="field" style={{ flex: "1 1 240px", minWidth: 200 }}>
              <label>Code</label>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="lowercase_with_underscores"
                disabled={editingId !== "new"}
              />
              <span className="mini muted">The code cannot be changed after saving.</span>
            </div>

            <label className="row" style={{ fontWeight: 400, flex: "0 0 auto", marginTop: 28 }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              <span>Active</span>
            </label>
          </div>

          {/* The question this form now leads with. Everything below depends
              on it, and the two destinations are genuinely exclusive — a
              radio pair says that where two checkboxes did not. */}
          <div className="field">
            <label>Publishes to</label>
            <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
              <label className="row" style={{ fontWeight: 400 }}>
                <input
                  type="radio"
                  name="destination"
                  checked={!form.publishesQuestions}
                  onChange={() => setForm({ ...form, publishesQuestions: false })}
                />
                <span>A content module</span>
              </label>
              <label className="row" style={{ fontWeight: 400 }}>
                <input
                  type="radio"
                  name="destination"
                  checked={form.publishesQuestions}
                  onChange={() =>
                    setForm({
                      ...form,
                      publishesQuestions: true,
                      // A question category's output has to be structured;
                      // there is no useful HTML form for a question the main
                      // app can store. Selecting this therefore implies json,
                      // which the server validates anyway — setting it here
                      // means the person is not bounced with an error for a
                      // choice they never consciously made.
                      responseType: "json",
                    })
                  }
                />
                <span>The question bank</span>
              </label>
            </div>
            <span className="mini muted">
              {form.publishesQuestions
                ? "Approved questions are written to the main app's question table with source = ai_generated. The chapter's existing book-back questions are sent to the model as an exclusion list, and any generated question that still repeats one is dropped before publishing."
                : "Approved content is written into a main-app content module as a titled section."}
            </span>
          </div>

          <div className="two-col">
            <div>

          <div className="field">
            <label>
              Prompt <span className="mini muted">or attach a document below</span>
            </label>
            <textarea
              value={form.prompt}
              onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              style={{ minHeight: 120 }}
            />

            {/* Always visible. The upload targets a category id, so on an
                unsaved category the button is disabled and says why, rather
                than the whole control being missing — a hidden feature reads
                as a bug. */}
            <div style={{ marginTop: 8 }}>
                <input
                  ref={promptDocInput}
                  type="file"
                  accept=".docx"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void uploadPromptDoc(editingId as number, file);
                  }}
                />

                {promptDoc ? (
                  <div className="row between" style={{ gap: 8, alignItems: "center" }}>
                    <span className="mini">
                      📄 {promptDoc.promptDocFilename}
                      <span className="muted">
                        {promptDoc.promptDocSizeBytes
                          ? ` · ${(promptDoc.promptDocSizeBytes / 1024).toFixed(0)} KB`
                          : ""}
                      </span>
                    </span>
                    <div className="row" style={{ gap: 6 }}>
                      <button
                        type="button"
                        className="btn mini"
                        title="View Word document"
                        onClick={() =>
                          setViewingDocx({
                            filename: promptDoc.promptDocFilename ?? "prompt-document.docx",
                            fileUrl: `/api/collections/${collection.id}/categories/${editingId}/prompt-doc/file`,
                            subtitle: `Category: ${form.name || "Prompt Document"}`,
                          })
                        }
                      >
                        View
                      </button>
                      <button
                        className="btn mini"
                        disabled={docBusy}
                        onClick={() => void removePromptDoc(editingId as number, promptDoc.promptDocFilename)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="btn mini"
                    disabled={docBusy || typeof editingId !== "number"}
                    title={
                      typeof editingId !== "number"
                        ? "Save the category first, then attach a document"
                        : undefined
                    }
                    onClick={() => promptDocInput.current?.click()}
                  >
                    {docBusy ? "Uploading…" : "Attach .docx"}
                  </button>
                )}

                <div className="mini muted" style={{ marginTop: 4 }}>
                  {typeof editingId === "number"
                    ? "Sent to the model with every generation for this category, exactly as uploaded."
                    : "Save this category first — the document attaches to it."}
                </div>
              </div>

            {/* A category with neither is saveable but cannot generate — the
                same rule the enqueue guard enforces, surfaced here so it is
                not discovered only when Generate fails. */}
            {!form.prompt.trim() && !promptDoc && (
              <Notice kind="warn">
                No prompt and no attached document — this category cannot generate until it has one.
              </Notice>
            )}
          </div>

            </div>

            <div>

          {/* Response type, module mapping and the sample belong to a
              content-module category only. A question category takes its shape
              from its question type, its destination is the question bank, and
              its schema is generated — showing these would offer choices that
              are then ignored. */}
          {!form.publishesQuestions && (
            <>
              <div className="field">
                <label>Response type</label>
                <select
                  value={form.responseType}
                  onChange={(e) => setForm({ ...form, responseType: e.target.value as ResponseType })}
                >
                  <option value="html">HTML</option>
                  <option value="json">JSON (structured, schema-enforced)</option>
                  <option value="markdown">Markdown</option>
                </select>
              </div>

              <div className="field">
                <label>Module (main app)</label>
                <select
                  value={form.externalModuleId ?? ""}
                  onChange={(e) => setForm({ ...form, externalModuleId: e.target.value || null })}
                >
                  <option value="">Not mapped yet</option>
                  {modules.data?.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.moduleType})
                    </option>
                  ))}
                </select>
                <span className="mini muted">
                  Where approved content from this category gets written in the main app. Only
                  text/explanation and assessment modules are shown — the ones ZenForge can write to.
                </span>
              </div>

              {form.responseType === "json" && (
                <div className="field">
                  <label>Output schema (JSON Schema)</label>
                  <textarea
                    value={form.outputSchemaText}
                    onChange={(e) => setForm({ ...form, outputSchemaText: e.target.value })}
                    style={{ minHeight: 140 }}
                    placeholder="A JSON Schema object describing the required shape"
                  />
                </div>
              )}

              {/* Collapsed by default: a sample is optional, and two more
                  controls plus a 140px textarea is a lot of height for
                  something most categories leave at "none". The summary shows
                  the mode so a configured sample is not hidden silently. */}
              <details open={form.sampleMode !== "none"}>
                <summary className="mini muted">
                  Sample output{form.sampleMode !== "none" ? ` · ${form.sampleMode}` : ""}
                </summary>

                <div className="field" style={{ marginTop: 8 }}>
                  <label>Sample mode</label>
                  <select
                    value={form.sampleMode}
                    onChange={(e) => setForm({ ...form, sampleMode: e.target.value as SampleMode })}
                  >
                    <option value="none">None</option>
                    <option value="exemplar">Exemplar — imitate structure and tone, different topic</option>
                    <option value="template">Template — fill in exactly, placeholders replaced</option>
                  </select>
                </div>

                <div className="field">
                  <label>Sample output</label>
                  <textarea
                    value={form.sampleOutput}
                    onChange={(e) => setForm({ ...form, sampleOutput: e.target.value })}
                    style={{ minHeight: 140 }}
                    placeholder={
                      form.responseType === "json"
                        ? "A complete example matching the schema"
                        : "A complete example output for a different topic"
                    }
                  />
                  <span className="mini muted">
                    An exemplar pushes the model to match structure directly instead of describing
                    it in prose — for HTML it is the only structural control available, since HTML
                    has no schema.
                  </span>
                </div>
              </details>
            </>
          )}

          {form.publishesQuestions && (
            <>
              <div className="field">
                <label>Question type</label>
                <select
                  value={form.questionAssessmentType}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      questionAssessmentType: e.target.value,
                      // A book-back category belongs to one assessment type,
                      // so a selection made under the previous type no longer
                      // applies. Left in place it would stay submitted while
                      // vanishing from the list below — a value the person can
                      // see is wrong but cannot clear.
                      questionCategoryId: "",
                    })
                  }
                >
                  {/* From the shared list, so the form and the validator
                      cannot disagree about which types exist. */}
                  {QUESTION_TYPES.map((type) => (
                    <option key={type.code} value={type.code}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <span className="mini muted">
                  Applied to every question this category publishes — the main app takes one type
                  per request, not one per question. It also decides the output schema the model is
                  held to.
                </span>
              </div>

              <div className="field">
                <label>Mark</label>
                <input
                  type="number"
                  min={1}
                  value={form.questionMarkText}
                  onChange={(e) => setForm({ ...form, questionMarkText: e.target.value })}
                />
              </div>

              <div className="field">
                <label>Importance (0–5)</label>
                <input
                  type="number"
                  min={0}
                  max={5}
                  value={form.questionImportanceText}
                  onChange={(e) => setForm({ ...form, questionImportanceText: e.target.value })}
                />
              </div>

              <div className="field">
                <label>Book back category (optional)</label>
                {questionCategories.error ? (
                  // The main app being unreachable must not block saving a
                  // category, so the raw id stays editable as a fallback
                  // rather than the field disappearing.
                  <>
                    <input
                      value={form.questionCategoryId}
                      onChange={(e) => setForm({ ...form, questionCategoryId: e.target.value })}
                      placeholder="Leave blank to file the questions under no category"
                    />
                    <Notice kind="warn">
                      Could not load categories from the main app, so they cannot be listed. Paste a
                      book_back_category id, or leave blank.
                    </Notice>
                  </>
                ) : (
                  <>
                    <select
                      value={form.questionCategoryId}
                      onChange={(e) => setForm({ ...form, questionCategoryId: e.target.value })}
                      disabled={questionCategories.loading}
                    >
                      <option value="">
                        {questionCategories.loading ? "Loading…" : "None"}
                      </option>
                      {matchingQuestionCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {!questionCategories.loading && matchingQuestionCategories.length === 0 && (
                      <span className="mini muted">
                        This syllabus has no{" "}
                        {QUESTION_TYPES.find((t) => t.code === form.questionAssessmentType)?.label ??
                          form.questionAssessmentType}{" "}
                        category in the main app. Questions can still be published without one.
                      </span>
                    )}
                  </>
                )}
                <span className="mini muted">
                  Groups these questions alongside the chapter&rsquo;s existing book-back questions.
                  Only categories matching the question type above are listed — the main app keys
                  them by assessment type.
                </span>
              </div>
            </>
          )}

          {/* Off for most categories, and four controls deep when on — so it
              collapses. Open when enabled, so an existing rubric is never
              hidden behind a closed summary. */}
          <details open={form.critiqueEnabled}>
            <summary className="mini muted">
              AI critique{form.critiqueEnabled ? " · on" : ""}
            </summary>

            <div className="field" style={{ marginTop: 8 }}>
              <label className="row" style={{ fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={form.critiqueEnabled}
                  onChange={(e) => setForm({ ...form, critiqueEnabled: e.target.checked })}
                />
                <span>Enabled</span>
              </label>
              <span className="mini muted">
                Runs automatically right after generation succeeds — a second AI pass that evaluates
                the content against a rubric and leaves feedback for the reviewer. It never rewrites
                or blocks anything; not every category needs it.
              </span>
            </div>

            {form.critiqueEnabled && (
              <>
                <div className="field">
                  <label>Critique prompt</label>
                  <textarea
                    value={form.critiquePrompt}
                    onChange={(e) => setForm({ ...form, critiquePrompt: e.target.value })}
                    style={{ minHeight: 100 }}
                    placeholder="The rubric the model should evaluate this category's content against"
                  />
                </div>

                <div className="field">
                  <label>Critique output</label>
                  <select
                    value={form.critiqueResponseType}
                    onChange={(e) =>
                      setForm({ ...form, critiqueResponseType: e.target.value as "text" | "json" })
                    }
                  >
                    <option value="text">Text — free-form feedback</option>
                    <option value="json">JSON — structured (e.g. a score breakdown)</option>
                  </select>
                </div>

                {form.critiqueResponseType === "json" && (
                  <div className="field">
                    <label>Critique output schema (JSON Schema)</label>
                    <textarea
                      value={form.critiqueOutputSchemaText}
                      onChange={(e) =>
                        setForm({ ...form, critiqueOutputSchemaText: e.target.value })
                      }
                      style={{ minHeight: 120 }}
                      placeholder="A JSON Schema object describing the required shape"
                    />
                  </div>
                )}
              </>
            )}
          </details>

            </div>
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn primary" onClick={() => void save()} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button className="btn" onClick={() => setEditingId(null)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {fileDeleteModal && (
        <FileRemovalModal modal={fileDeleteModal} onClose={() => setFileDeleteModal(null)} />
      )}
      {viewingDocx && (
        <DocxViewerModal
          filename={viewingDocx.filename}
          fileUrl={viewingDocx.fileUrl}
          subtitle={viewingDocx.subtitle}
          onClose={() => setViewingDocx(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Run — the direct test loop
// ---------------------------------------------------------------------------

function RunTab({
  collection,
  toast,
}: {
  collection: CollectionDetailData;
  toast: (msg: string) => void;
}) {
  const activeCategories = collection.categories.filter((c) => c.isActive);
  const [categoryId, setCategoryId] = useState<number | null>(activeCategories[0]?.id ?? null);
  const [target, setTarget] = useState("");
  const [allowGeneralKnowledge, setAllowGeneralKnowledge] = useState(collection.allowGeneralKnowledge);
  const [selectedSources, setSelectedSources] = useState<Set<number>>(new Set());
  const [runCritique, setRunCritique] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const [rawView, setRawView] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const category = collection.categories.find((c) => c.id === categoryId);
  const readySources = collection.knowledgeSources.filter((s) => s.indexingStatus === "ready");
  const history = useApi<TestRunRow[]>(`/collections/${collection.id}/run`, [historyKey]);

  function toggleSource(id: number) {
    const next = new Set(selectedSources);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedSources(next);
  }

  function openHistoryRow(row: TestRunRow) {
    setCategoryId(row.categoryId);
    setTarget(row.target);
    setResult({
      content: row.content,
      model: row.model,
      inputTokens: row.inputTokens,
      outputTokens: row.outputTokens,
      cachedTokens: row.cachedTokens,
      durationMs: row.durationMs,
      retrievedChunks: row.retrievedChunks ?? 0,
      searchCalls: row.searchCalls ?? 0,
      notInTextbook: row.notInTextbook,
      ungrounded: row.ungrounded,
      estimatedCost: row.estimatedCost === null ? null : Number(row.estimatedCost),
      critique: row.critiqueFeedback
        ? {
            feedback: row.critiqueFeedback,
            model: row.critiqueModel ?? row.model,
            inputTokens: row.critiqueInputTokens ?? 0,
            outputTokens: row.critiqueOutputTokens ?? 0,
            estimatedCost:
              row.critiqueEstimatedCost === null ? null : Number(row.critiqueEstimatedCost),
            durationMs: row.critiqueDurationMs ?? 0,
          }
        : null,
      critiqueError: null,
    });
  }

  async function run() {
    if (!categoryId || !target.trim()) {
      toast("Choose a category and enter a topic");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const response = await api.post<RunResult>(`/collections/${collection.id}/run`, {
        categoryId,
        target: target.trim(),
        allowGeneralKnowledge,
        knowledgeSourceIds: [...selectedSources],
        runCritique: runCritique && category?.critiqueEnabled,
      });
      setResult(response);
      setHistoryKey((k) => k + 1);
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Generation failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="grid">
      <div className="card span-5">
        <h2>Run</h2>

        {activeCategories.length === 0 && (
          <Notice kind="warn">No active categories — add one in the Categories tab first.</Notice>
        )}

        <div className="field">
          <label>Category</label>
          <select
            value={categoryId ?? ""}
            onChange={(e) => {
              setCategoryId(Number(e.target.value));
              setRunCritique(false);
            }}
          >
            {activeCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.responseType})
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Topic</label>
          <input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="e.g. Laws of Reflection"
          />
        </div>

        <label className="row" style={{ fontWeight: 400 }}>
          <input
            type="checkbox"
            checked={allowGeneralKnowledge}
            onChange={(e) => setAllowGeneralKnowledge(e.target.checked)}
          />
          <span className="muted">
            Allow general knowledge. When off, the model must use the knowledge sources only.
          </span>
        </label>

        {!collection.vectorStoreId && !allowGeneralKnowledge && (
          <Notice kind="warn">
            This collection has no knowledge sources yet, and general knowledge is off — every run
            will report the topic as not covered.
          </Notice>
        )}

        {readySources.length > 1 && (
          <div className="field">
            <label>Search within</label>
            {readySources.map((source) => (
              <label
                key={source.id}
                className="row"
                style={{ fontWeight: 400, opacity: source.filterable ? 1 : 0.5 }}
              >
                <input
                  type="checkbox"
                  checked={selectedSources.has(source.id)}
                  disabled={!source.filterable}
                  onChange={() => toggleSource(source.id)}
                />
                <span className="muted">
                  {source.filename}
                  {/* Both are unsearchable, for different reasons — saying
                      "uploaded before filtering existed" about an attached
                      file sends people looking for a problem to fix. */}
                  {!source.filterable &&
                    (source.retrievalMode === "direct_attach"
                      ? " — attached whole, not searched"
                      : " — uploaded before source filtering existed")}
                </span>
              </label>
            ))}
            <span className="mini muted">
              Nothing checked searches every knowledge source in the collection.
            </span>
          </div>
        )}

        {category?.critiqueEnabled && (
          <label className="row" style={{ fontWeight: 400 }}>
            <input
              type="checkbox"
              checked={runCritique}
              onChange={(e) => setRunCritique(e.target.checked)}
            />
            <span className="muted">
              Also run this category's AI critique pass against the result.
            </span>
          </label>
        )}

        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn primary" onClick={() => void run()} disabled={running || !category}>
            {running ? "Generating…" : "Run"}
          </button>
        </div>
      </div>

      <div className="card span-7">
        <h2>Result</h2>

        {!result && !running && <p className="muted">Run a generation to see the output here.</p>}
        {running && <p className="muted">Generating…</p>}

        {result && (
          <>
            {result.notInTextbook && (
              <Notice kind="warn">
                <strong>Not in the knowledge sources.</strong> The model reported this topic is not
                covered and declined to invent content.
              </Notice>
            )}
            {result.ungrounded && (
              <Notice kind="error">
                <strong>Ungrounded.</strong> Nothing was retrieved — this came from the model's own
                knowledge. Verify it before trusting it.
              </Notice>
            )}

            <div className="row" style={{ gap: 20, marginBottom: 10 }}>
              <span className="mini muted">model: {result.model}</span>
              <span className="mini muted">
                tokens: {result.inputTokens} in / {result.outputTokens} out
                {result.cachedTokens > 0 ? ` (${result.cachedTokens} cached)` : ""}
              </span>
              <span className="mini muted">
                cost: {result.estimatedCost === null ? "—" : `$${result.estimatedCost.toFixed(4)}`}
              </span>
              <span className="mini muted">{formatDuration(result.durationMs)}</span>
              <span className="mini muted">
                retrieved: {result.retrievedChunks} chunks / {result.searchCalls} search
                {result.searchCalls === 1 ? "" : "es"}
              </span>
            </div>

            {result.notInTextbook ? (
              // The refusal marker is plain text, not html or json — showing it
              // through either renderer would either strip it or flag it as a
              // parse failure, when it is actually the expected outcome.
              <pre className="preview">{result.content}</pre>
            ) : category?.responseType === "html" ? (
              <>
                <div className="editor-tabs">
                  <button className={!rawView ? "active" : ""} onClick={() => setRawView(false)}>
                    Preview
                  </button>
                  <button className={rawView ? "active" : ""} onClick={() => setRawView(true)}>
                    HTML
                  </button>
                </div>
                {rawView ? (
                  <textarea readOnly value={result.content} />
                ) : (
                  <ContentPreview content={result.content} responseType="html" />
                )}
              </>
            ) : (
              <ContentPreview
                content={result.content}
                responseType={category?.responseType ?? "markdown"}
              />
            )}

            {result.critique && (
              <div className="book" style={{ marginTop: 16 }}>
                <div className="row between">
                  <b className="mini muted">AI CRITIQUE</b>
                  <span className="mini muted">
                    {result.critique.inputTokens} in / {result.critique.outputTokens} out ·{" "}
                    {result.critique.estimatedCost === null
                      ? "—"
                      : `$${result.critique.estimatedCost.toFixed(4)}`}{" "}
                    · {formatDuration(result.critique.durationMs)}
                  </span>
                </div>
                {category?.critiqueResponseType === "json" ? (
                  <pre className="preview" style={{ marginTop: 6 }}>{result.critique.feedback}</pre>
                ) : (
                  <p style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{result.critique.feedback}</p>
                )}
              </div>
            )}

            {result.critiqueError && (
              <Notice kind="warn">
                <strong>Critique failed.</strong> {result.critiqueError} The generation above still
                succeeded and was saved.
              </Notice>
            )}
          </>
        )}
      </div>

      <div className="card span-12">
        <h2>Recent test runs</h2>
        {history.data?.length === 0 && <p className="muted">Nothing run yet in this collection.</p>}
        {history.data && history.data.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Category</th>
                <th>Topic</th>
                <th>Cost</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.data.map((row) => (
                <tr key={row.id}>
                  <td className="mini muted">{new Date(row.createdAt).toLocaleString()}</td>
                  <td>{row.category?.name ?? "—"}</td>
                  <td>{row.target}</td>
                  <td>
                    {formatCost(Number(row.estimatedCost ?? 0) + Number(row.critiqueEstimatedCost ?? 0))}
                    {row.critiqueFeedback && <span className="mini muted"> (+critique)</span>}
                  </td>
                  <td>
                    <button className="btn mini" onClick={() => openHistoryRow(row)}>
                      Load
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

interface PipelineStepInput {
  name: string;
  stepOrder: number;
  stepType?: "LLM" | "INTERNAL_TRANSFORM";
  transformType?: "VARIABLE_REPLACEMENT" | "EXTRACT_TO_REVIEWER_NOTES" | null;
  transformConfig?: {
    template?: string;
    variableMappings?: {
      varName: string;
      sourceType: "STATIC_VALUE" | "STEP_OUTPUT" | "TARGET_CONTEXT" | "PIPELINE_INPUT";
      stepOrder?: number;
      staticValue?: string;
      contextKey?: string;
    }[];
    extractPattern?: string;
    extractSourceStepOrder?: number;
    writeToReviewerNotes?: boolean;
  } | null;
  requestedModel: string;
  prompt: string;
  promptDocFileId?: string | null;
  promptDocFilename?: string | null;
  promptDocSizeBytes?: number | null;
  enableRetrieval: boolean;
  isFinalOutput?: boolean;
  inputMappings: { varName: string; sourceType: "STEP_OUTPUT" | "STATIC_VALUE" | "TARGET_CONTEXT" | "PIPELINE_INPUT"; stepOrder?: number; staticValue?: string }[];
}

function PipelinesTab({
  collectionId,
  collectionModel,
  toast,
}: {
  collectionId: number;
  collectionModel?: string | null;
  toast: (m: string) => void;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const pipelines = useApi<any[]>(`/pipelines/collection/${collectionId}`, [refreshKey]);
  const models = useApi<string[]>("/models");
  const modules = useApi<MainAppModule[]>("/modules");
  const { isPending, run } = usePendingActions();

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [fileDeleteModal, setFileDeleteModal] = useState<FileDeleteModalState | null>(null);
  const [viewingDocx, setViewingDocx] = useState<{ filename: string; fileUrl: string; subtitle?: string } | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [defaultModel, setDefaultModel] = useState("");
  const [autoApprove, setAutoApprove] = useState<boolean>(false);
  // Where this pipeline sits in the picker. Not execution order — that comes
  // from the steps below.
  const [displayOrder, setDisplayOrder] = useState<string>("0");
  const [pipelineIsActive, setPipelineIsActive] = useState<boolean>(true);
  const [externalModuleId, setExternalModuleId] = useState<string | null>(null);
  const [steps, setSteps] = useState<PipelineStepInput[]>([
    {
      name: "Step 1: Generate Draft",
      stepOrder: 1,
      stepType: "LLM",
      requestedModel: "",
      prompt: "Generate detailed explanation content for the target topic.",
      promptDocFileId: null,
      promptDocFilename: null,
      promptDocSizeBytes: null,
      enableRetrieval: true,
      inputMappings: [],
    },
    {
      name: "Step 2: Convert to HTML",
      stepOrder: 2,
      stepType: "LLM",
      requestedModel: "",
      prompt: "Convert the following draft explanation into clean HTML with appropriate headings, paragraphs, and lists.",
      promptDocFileId: null,
      promptDocFilename: null,
      promptDocSizeBytes: null,
      enableRetrieval: false,
      isFinalOutput: true,
      inputMappings: [{ varName: "draft_content", sourceType: "STEP_OUTPUT", stepOrder: 1 }],
    },
  ]);

  const [editingStepIdx, setEditingStepIdx] = useState<number | null>(null);

  function moveStep(fromIndex: number, toIndex: number) {
    if (fromIndex < 0 || fromIndex >= steps.length || toIndex < 0 || toIndex >= steps.length) return;
    const nextSteps = [...steps];
    const [moved] = nextSteps.splice(fromIndex, 1);
    if (!moved) return;
    nextSteps.splice(toIndex, 0, moved);
    const reindexed = nextSteps.map((st, i) => ({ ...st, stepOrder: i + 1 }));
    setSteps(reindexed);
  }

  function resetForm() {
    setName("");
    setDescription("");
    setDefaultModel("");
    setAutoApprove(false);
    setDisplayOrder("0");
    setPipelineIsActive(true);
    setExternalModuleId(null);
    setSteps([
      {
        name: "Step 1: Generate Draft",
        stepOrder: 1,
        stepType: "LLM",
        requestedModel: "",
        prompt: "Generate detailed explanation content for the target topic.",
        promptDocFileId: null,
        promptDocFilename: null,
        promptDocSizeBytes: null,
        enableRetrieval: true,
        inputMappings: [],
      },
      {
        name: "Step 2: Convert to HTML",
        stepOrder: 2,
        stepType: "LLM",
        requestedModel: "",
        prompt: "Convert the following draft explanation into clean HTML format.",
        promptDocFileId: null,
        promptDocFilename: null,
        promptDocSizeBytes: null,
        enableRetrieval: false,
        isFinalOutput: true,
        inputMappings: [{ varName: "draft_content", sourceType: "STEP_OUTPUT", stepOrder: 1 }],
      },
    ]);
    setEditingId(null);
    setEditingStepIdx(null);
    setCreating(false);
  }

  function editPipeline(p: any) {
    setName(p.name);
    setDescription(p.description ?? "");
    setDefaultModel(p.defaultModel ?? "");
    setAutoApprove(Boolean(p.autoApprove));
    setDisplayOrder(String(p.displayOrder ?? 0));
    setPipelineIsActive(p.isActive !== false);
    setExternalModuleId(p.externalModuleId ?? null);
    setSteps(
      p.steps?.map((s: any) => ({
        name: s.name,
        stepOrder: s.stepOrder,
        stepType: s.stepType ?? "LLM",
        transformType: s.transformType ?? null,
        transformConfig: s.transformConfig ?? null,
        // Null, not "": the empty string is what a <select> holds for the
        // "inherit" option, but the server resolves models with `??`, which
        // does not fall through an empty string — so a blank sent as "" beat
        // the run and collection models and reached OpenAI as model: "".
        requestedModel: s.requestedModel?.trim() ? s.requestedModel.trim() : null,
        prompt: s.prompt ?? "",
        promptDocFileId: s.promptDocFileId ?? null,
        promptDocFilename: s.promptDocFilename ?? null,
        promptDocSizeBytes: s.promptDocSizeBytes ?? null,
        enableRetrieval: Boolean(s.enableRetrieval),
        isFinalOutput: Boolean(s.isFinalOutput),
        inputMappings: s.inputMappings ?? [],
      })) ?? [],
    );
    setEditingId(p.id);
    setEditingStepIdx(null);
    setCreating(true);
  }

  async function savePipeline() {
    if (!name.trim()) {
      toast("Pipeline name is required");
      return;
    }
    if (steps.length === 0) {
      toast("Add at least one step to the pipeline");
      return;
    }

    try {
      if (editingId) {
        await run("pipeline-save", () =>
          api.put(`/pipelines/${editingId}`, {
            name,
            description,
            defaultModel: defaultModel.trim() ? defaultModel.trim() : null,
            autoApprove,
            displayOrder: Number(displayOrder) || 0,
            isActive: pipelineIsActive,
            externalModuleId,
            steps,
          }),
        );
        toast("Pipeline updated");
      } else {
        await run("pipeline-save", () =>
          api.post(`/pipelines/collection/${collectionId}`, {
            name,
            description,
            defaultModel: defaultModel.trim() ? defaultModel.trim() : null,
            autoApprove,
            displayOrder: Number(displayOrder) || 0,
            isActive: pipelineIsActive,
            externalModuleId,
            steps,
          }),
        );
        toast("Pipeline created");
      }
      resetForm();
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast(err.message || "Failed to save pipeline");
    }
  }

  async function handleClonePipeline(pipelineId: number) {
    try {
      const cloned = await run(`pipeline-${pipelineId}-clone`, () =>
        api.post<{ id: number; name: string }>(`/pipelines/${pipelineId}/clone`, {}),
      );
      toast(`Cloned pipeline as "${cloned.name}"`);
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast(err.message || "Failed to clone pipeline");
    }
  }

  async function handleDeletePipeline(pipelineId: number, pipelineName: string) {
    if (!window.confirm(`Are you sure you want to delete pipeline "${pipelineName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await run(`pipeline-${pipelineId}-delete`, () => api.delete(`/pipelines/${pipelineId}`));
      toast(`Deleted pipeline "${pipelineName}"`);
      if (editingId === pipelineId) {
        resetForm();
      }
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast(err.message || "Failed to delete pipeline");
    }
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="row between mb-4" style={{ marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0 }}>Collection Pipelines</h3>
          <div className="muted mini">Configure multi-step generation pipelines for this collection</div>
        </div>
        {!creating && (
          <button className="btn primary" onClick={() => { resetForm(); setCreating(true); }}>
            + Create Pipeline
          </button>
        )}
      </div>

      {creating && (
        <div className="book" style={{ padding: 16, marginBottom: 16, borderRadius: 8 }}>
          <h4 style={{ marginTop: 0 }}>{editingId ? "Edit Pipeline" : "Create New Pipeline"}</h4>
          <div className="row" style={{ gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
            <div className="field" style={{ flex: 1, minWidth: 200 }}>
              <label>Pipeline Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Draft -> HTML -> Review" />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 200 }}>
              <label>Pipeline Default Model</label>
              <select value={defaultModel} onChange={(e) => setDefaultModel(e.target.value)}>
                <option value="">Inherit from Collection ({collectionModel ?? "default"})</option>
                {models.data?.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 200 }}>
              <label>Publish Module Destination (Main App)</label>
              <select value={externalModuleId ?? ""} onChange={(e) => setExternalModuleId(e.target.value || null)}>
                <option value="">None / Select module…</option>
                {modules.data?.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} ({m.moduleType})</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 180 }}>
              <label>Approval Workflow</label>
              <select value={autoApprove ? "auto" : "manual"} onChange={(e) => setAutoApprove(e.target.value === "auto")}>
                <option value="manual">📋 Manual Approval</option>
                <option value="auto">⚡ Auto-Approve & Push</option>
              </select>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 140 }}>
              <label>List Order</label>
              <input
                type="number"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(e.target.value)}
                min={0}
              />
              <small className="muted">
                Position in the pipeline picker. Lower shows first; ties fall back to
                newest. Does not affect the order steps run in.
              </small>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 160 }}>
              <label>Status</label>
              <select
                value={pipelineIsActive ? "active" : "inactive"}
                onChange={(e) => setPipelineIsActive(e.target.value === "active")}
              >
                <option value="active">Active (runnable)</option>
                <option value="inactive">Inactive (hidden)</option>
              </select>
            </div>
          </div>
          <div className="field" style={{ marginBottom: 16 }}>
            <label>Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description of this pipeline" />
          </div>

          <h5 style={{ marginBottom: 12 }}>Pipeline Flowchart Steps ({steps.length})</h5>

          <div className="pipeline-flow-container">
            {steps.map((st, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <div className="pipeline-flow-arrow">➔</div>}
                <div className="pipeline-step-card">
                  <div className="pipeline-step-header">
                    <span className="pipeline-step-order">#{st.stepOrder}</span>
                    <div className="pipeline-step-actions">
                      <button
                        type="button"
                        className="btn mini icon-btn"
                        title="Move step left"
                        disabled={idx === 0}
                        onClick={() => moveStep(idx, idx - 1)}
                      >
                        ◀
                      </button>
                      <button
                        type="button"
                        className="btn mini icon-btn"
                        title="Move step right"
                        disabled={idx === steps.length - 1}
                        onClick={() => moveStep(idx, idx + 1)}
                      >
                        ▶
                      </button>
                      {steps.length > 1 && (
                        <button
                          type="button"
                          className="btn mini icon-btn danger"
                          title="Remove step"
                          onClick={() => {
                            const next = steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepOrder: i + 1 }));
                            setSteps(next);
                            if (editingStepIdx === idx) setEditingStepIdx(null);
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="pipeline-step-name">{st.name || `Step ${st.stepOrder}`}</div>

                  <div className="pipeline-step-badges">
                    {st.stepType === "INTERNAL_TRANSFORM" ? (
                      <span className="badge success mini" style={{ backgroundColor: "#2e7d32", color: "#fff" }}>
                        ⚡ Internal Transform
                      </span>
                    ) : st.requestedModel ? (
                      <span className="badge pending mini">{st.requestedModel}</span>
                    ) : (
                      <span className="badge mini muted">Default Model</span>
                    )}
                    {st.promptDocFileId && (
                      <span
                        className="badge success mini"
                        style={{ cursor: "pointer" }}
                        title={`Attached: ${st.promptDocFilename ?? "Prompt Doc"} — click to view`}
                        onClick={(e) => {
                          e.stopPropagation();
                          const filename = st.promptDocFilename ?? "prompt-document.docx";
                          setViewingDocx({
                            filename,
                            fileUrl: `/api/pipelines/prompt-doc/${st.promptDocFileId}/file?filename=${encodeURIComponent(filename)}`,
                            subtitle: `Step: ${st.name || `Step #${idx + 1}`}`,
                          });
                        }}
                      >
                        📄 {st.promptDocFilename ? st.promptDocFilename.slice(0, 15) + "…" : "Doc"}
                      </span>
                    )}
                    {st.enableRetrieval && (
                      <span className="badge info mini">📚 Vector PDF</span>
                    )}
                    {st.isFinalOutput && (
                      <span className="badge generated mini" title="Flagged as Final Content Output">⭐ Final Content</span>
                    )}
                    {st.inputMappings.length > 0 && (
                      <span className="badge mini muted">📥 {st.inputMappings.length} input(s)</span>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn mini primary fit"
                    style={{ marginTop: "auto" }}
                    onClick={() => setEditingStepIdx(idx)}
                  >
                    ⚙ Configure Step
                  </button>
                </div>
              </React.Fragment>
            ))}

            <div
              className="pipeline-step-add-card"
              onClick={() => {
                const newStep: PipelineStepInput = {
                  name: `Step ${steps.length + 1}`,
                  stepOrder: steps.length + 1,
                  stepType: "LLM",
                  requestedModel: "",
                  prompt: "",
                  promptDocFileId: null,
                  promptDocFilename: null,
                  promptDocSizeBytes: null,
                  enableRetrieval: false,
                  isFinalOutput: false,
                  inputMappings: steps.length > 0 ? [{ varName: "previous_output", sourceType: "STEP_OUTPUT", stepOrder: steps.length }] : [],
                };
                setSteps([...steps, newStep]);
                setEditingStepIdx(steps.length);
              }}
            >
              <div style={{ fontSize: "1.5rem", marginBottom: 4 }}>+</div>
              <div>Add Step</div>
            </div>
          </div>

          {editingStepIdx !== null && steps[editingStepIdx] && (() => {
            const currentStep = steps[editingStepIdx];
            if (!currentStep) return null;
            const isInternal = currentStep.stepType === "INTERNAL_TRANSFORM";
            const isExtractToNotes = isInternal && currentStep.transformType === "EXTRACT_TO_REVIEWER_NOTES";
            const executionTypeValue: "LLM" | "VARIABLE_REPLACEMENT" | "EXTRACT_TO_REVIEWER_NOTES" = !isInternal
              ? "LLM"
              : isExtractToNotes
                ? "EXTRACT_TO_REVIEWER_NOTES"
                : "VARIABLE_REPLACEMENT";

            return (
              <div className="modal-backdrop" onClick={() => setEditingStepIdx(null)}>
                <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                  <div className="row between" style={{ marginBottom: 16 }}>
                    <h4 style={{ margin: 0 }}>
                      Configure Step #{currentStep.stepOrder}: {currentStep.name}
                    </h4>
                    <button type="button" className="btn mini" onClick={() => setEditingStepIdx(null)}>
                      ✕ Close
                    </button>
                  </div>

                  <div className="field" style={{ marginBottom: 12 }}>
                    <label>Step Execution Type</label>
                    <select
                      value={executionTypeValue}
                      onChange={(e) => {
                        const val = e.target.value as "LLM" | "VARIABLE_REPLACEMENT" | "EXTRACT_TO_REVIEWER_NOTES";
                        setSteps((prev) =>
                          prev.map((s, i) => {
                            if (i !== editingStepIdx) return s;
                            if (val === "LLM") {
                              return { ...s, stepType: "LLM", transformType: null, transformConfig: null };
                            }
                            if (val === "VARIABLE_REPLACEMENT") {
                              return {
                                ...s,
                                stepType: "INTERNAL_TRANSFORM",
                                transformType: "VARIABLE_REPLACEMENT",
                                transformConfig: {
                                  template: s.prompt || "<html>\n<head>\n  <style>{{STATIC_STYLE}}</style>\n</head>\n<body>\n  {{BODY_CONTENT}}\n</body>\n</html>",
                                  variableMappings: s.inputMappings?.map((m) => ({
                                    varName: m.varName,
                                    sourceType: m.sourceType as any,
                                    stepOrder: m.stepOrder,
                                    staticValue: m.staticValue,
                                  })) ?? [],
                                },
                              };
                            }
                            // EXTRACT_TO_REVIEWER_NOTES
                            return {
                              ...s,
                              stepType: "INTERNAL_TRANSFORM",
                              transformType: "EXTRACT_TO_REVIEWER_NOTES",
                              transformConfig: {
                                extractPattern: s.transformConfig?.extractPattern ?? "",
                                extractSourceStepOrder:
                                  s.transformConfig?.extractSourceStepOrder ?? Math.max(1, s.stepOrder - 1),
                                writeToReviewerNotes: s.transformConfig?.writeToReviewerNotes ?? true,
                              },
                            };
                          }),
                        );
                      }}
                    >
                      <option value="LLM">🤖 OpenAI LLM Model Call</option>
                      <option value="VARIABLE_REPLACEMENT">⚡ Generic Variable Replacement (Internal Step - 0 tokens, $0.00)</option>
                      <option value="EXTRACT_TO_REVIEWER_NOTES">📝 Extract Section to Reviewer Notes (Internal Step - 0 tokens, $0.00)</option>
                    </select>
                  </div>

                  <div className="field" style={{ marginBottom: 12 }}>
                    <label>Step Name</label>
                    <input
                      value={currentStep.name}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSteps((prev) => prev.map((s, i) => (i === editingStepIdx ? { ...s, name: val } : s)));
                      }}
                    />
                  </div>

                  {!isInternal && (
                    <>
                      <div className="field" style={{ marginBottom: 12 }}>
                        <label>Model Override</label>
                        <select
                          value={currentStep.requestedModel}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSteps((prev) => prev.map((s, i) => (i === editingStepIdx ? { ...s, requestedModel: val } : s)));
                          }}
                        >
                          <option value="">Collection default model</option>
                          {models.data?.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="field" style={{ marginBottom: 12 }}>
                        <label>System Prompt / Instructions</label>
                        <textarea
                          rows={4}
                          value={currentStep.prompt}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSteps((prev) => prev.map((s, i) => (i === editingStepIdx ? { ...s, prompt: val } : s)));
                          }}
                          placeholder="Type step instructions or prompt logic..."
                        />
                      </div>

                      <div className="field" style={{ marginBottom: 16 }}>
                        <label>Prompt Document (.docx file)</label>
                        {currentStep.promptDocFileId ? (
                          <div className="row" style={{ gap: 10, alignItems: "center" }}>
                            <span className="badge pending mini" style={{ fontSize: "0.85rem" }}>
                              📄 {currentStep.promptDocFilename ?? currentStep.promptDocFileId}
                              {currentStep.promptDocSizeBytes
                                ? ` (${(currentStep.promptDocSizeBytes / 1024).toFixed(1)} KB)`
                                : ""}
                            </span>
                            <button
                              type="button"
                              className="btn mini"
                              title="View Word document"
                              onClick={() => {
                                const docId = currentStep.promptDocFileId!;
                                const filename = currentStep.promptDocFilename ?? "prompt-document.docx";
                                setViewingDocx({
                                  filename,
                                  fileUrl: `/api/pipelines/prompt-doc/${docId}/file?filename=${encodeURIComponent(filename)}`,
                                  subtitle: `Step: ${currentStep.name || `Step #${editingStepIdx + 1}`}`,
                                });
                              }}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              className="btn mini danger"
                              onClick={() => {
                                const docId = currentStep.promptDocFileId;
                                const filename = currentStep.promptDocFilename ?? "Prompt Document";
                                setFileDeleteModal({
                                  title: "Remove Pipeline Step Prompt Document",
                                  filename,
                                  onConfirm: async (purgeRemote: boolean) => {
                                    try {
                                      if (docId) {
                                        await api.delete(`/pipelines/prompt-doc/${docId}?purgeRemote=${purgeRemote}&collectionId=${collectionId}`).catch(() => null);
                                      }
                                      setSteps((prev) =>
                                        prev.map((s, i) =>
                                          i === editingStepIdx
                                            ? { ...s, promptDocFileId: null, promptDocFilename: null, promptDocSizeBytes: null }
                                            : s,
                                        ),
                                      );
                                      toast(`Attached document removed ${purgeRemote ? "(from DB & OpenAI)" : "(from DB only)"}`);
                                    } catch (error: unknown) {
                                      toast(error instanceof Error ? error.message : "Remove failed");
                                    }
                                  },
                                });
                              }}
                            >
                              Remove Document
                            </button>
                          </div>
                        ) : (
                          <div className="row" style={{ gap: 8, alignItems: "center" }}>
                            <input
                              type="file"
                              accept=".docx"
                              style={{ display: "none" }}
                              id={`modal-step-doc-file-${editingStepIdx}`}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  const res = await uploadFile<{ fileId: string; filename: string; sizeBytes: number }>(
                                    `/pipelines/prompt-doc?collectionId=${collectionId}`,
                                    file,
                                  );
                                  setSteps((prev) =>
                                    prev.map((s, i) =>
                                      i === editingStepIdx
                                        ? {
                                            ...s,
                                            promptDocFileId: res.fileId,
                                            promptDocFilename: res.filename,
                                            promptDocSizeBytes: res.sizeBytes,
                                          }
                                        : s,
                                    ),
                                  );
                                  toast(`Attached ${file.name} to Step ${currentStep.stepOrder}`);
                                } catch (err: any) {
                                  toast(err.message || "Failed to upload document");
                                } finally {
                                  e.target.value = "";
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="btn mini"
                              onClick={() => {
                                document.getElementById(`modal-step-doc-file-${editingStepIdx}`)?.click();
                              }}
                            >
                              📎 Attach .docx prompt document
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {isExtractToNotes && (
                    <>
                      <div className="field" style={{ marginBottom: 12 }}>
                        <label>Source Step</label>
                        <select
                          value={currentStep.transformConfig?.extractSourceStepOrder ?? Math.max(1, currentStep.stepOrder - 1)}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            setSteps((prev) =>
                              prev.map((s, i) =>
                                i === editingStepIdx
                                  ? { ...s, transformConfig: { ...(s.transformConfig ?? {}), extractSourceStepOrder: val } }
                                  : s,
                              ),
                            );
                          }}
                        >
                          {steps
                            .filter((s) => s.stepOrder < currentStep.stepOrder)
                            .map((s) => (
                              <option key={s.stepOrder} value={s.stepOrder}>
                                Step {s.stepOrder}: {s.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      <div className="field" style={{ marginBottom: 16 }}>
                        <label>Extraction Pattern (regex, optional)</label>
                        <input
                          style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
                          value={currentStep.transformConfig?.extractPattern ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSteps((prev) =>
                              prev.map((s, i) =>
                                i === editingStepIdx
                                  ? { ...s, transformConfig: { ...(s.transformConfig ?? {}), extractPattern: val } }
                                  : s,
                              ),
                            );
                          }}
                          placeholder={"e.g. <!--NOTES_START-->([\\s\\S]*?)<!--NOTES_END--> — capture group 1 is used, or the whole match if there is none"}
                        />
                        <div className="hint" style={{ fontSize: "0.8rem", marginTop: 4 }}>
                          Applied to the source step's output. The matched section is cut out of this step's
                          output, so later steps see the remainder. Leave blank to remove the entire source
                          output (this step then produces nothing for later steps).
                        </div>
                      </div>

                      <div className="field" style={{ marginBottom: 16 }}>
                        <label className="row" style={{ gap: 6, cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={currentStep.transformConfig?.writeToReviewerNotes ?? true}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSteps((prev) =>
                                prev.map((s, i) =>
                                  i === editingStepIdx
                                    ? { ...s, transformConfig: { ...(s.transformConfig ?? {}), writeToReviewerNotes: checked } }
                                    : s,
                                ),
                              );
                            }}
                          />
                          <span>Write removed section to reviewer notes</span>
                        </label>
                        <div className="hint" style={{ fontSize: "0.8rem", marginTop: 4 }}>
                          On: the removed section overwrites this execution's reviewer note. Off: the section is
                          simply removed from the content and discarded.
                        </div>
                      </div>
                    </>
                  )}

                  {isInternal && !isExtractToNotes && (
                    <>
                      <div className="field" style={{ marginBottom: 12 }}>
                        <label>Template Text (use {`{{varName}}`} or {`${`\${varName}`}`} placeholders)</label>
                        <textarea
                          rows={6}
                          style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
                          value={currentStep.transformConfig?.template ?? currentStep.prompt ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSteps((prev) =>
                              prev.map((s, i) =>
                                i === editingStepIdx
                                  ? {
                                      ...s,
                                      prompt: val,
                                      transformConfig: { ...(s.transformConfig ?? {}), template: val },
                                    }
                                  : s,
                              ),
                            );
                          }}
                          placeholder="Enter template text with {{varName}} placeholders..."
                        />
                      </div>

                      <div className="field" style={{ marginBottom: 16 }}>
                        <div className="row between" style={{ marginBottom: 8 }}>
                          <label style={{ margin: 0 }}>Variable Replacement Mappings</label>
                          <button
                            type="button"
                            className="btn mini primary"
                            onClick={() => {
                              const curMappings = currentStep.transformConfig?.variableMappings ?? [];
                              const newMappings = [
                                ...curMappings,
                                { varName: `var_${curMappings.length + 1}`, sourceType: "STATIC_VALUE" as const, staticValue: "" },
                              ];
                              setSteps((prev) =>
                                prev.map((s, i) =>
                                  i === editingStepIdx
                                    ? {
                                        ...s,
                                        inputMappings: newMappings as any,
                                        transformConfig: { ...(s.transformConfig ?? {}), variableMappings: newMappings },
                                      }
                                    : s,
                                ),
                              );
                            }}
                          >
                            + Add Variable
                          </button>
                        </div>

                        {(currentStep.transformConfig?.variableMappings ?? []).map((vMap, vmIdx) => (
                          <div key={vmIdx} className="card" style={{ padding: 10, marginBottom: 8, background: "var(--bg-subtle, #f8f9fa)" }}>
                            <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                              <input
                                style={{ width: 140 }}
                                placeholder="varName"
                                value={vMap.varName}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const curMappings = [...(currentStep.transformConfig?.variableMappings ?? [])];
                                  curMappings[vmIdx] = { ...curMappings[vmIdx]!, varName: val };
                                  setSteps((prev) =>
                                    prev.map((s, i) =>
                                      i === editingStepIdx
                                        ? { ...s, inputMappings: curMappings as any, transformConfig: { ...(s.transformConfig ?? {}), variableMappings: curMappings } }
                                        : s,
                                    ),
                                  );
                                }}
                              />
                              <select
                                style={{ width: 160 }}
                                value={vMap.sourceType}
                                onChange={(e) => {
                                  const val = e.target.value as any;
                                  const curMappings = [...(currentStep.transformConfig?.variableMappings ?? [])];
                                  curMappings[vmIdx] = { ...curMappings[vmIdx]!, sourceType: val };
                                  setSteps((prev) =>
                                    prev.map((s, i) =>
                                      i === editingStepIdx
                                        ? { ...s, inputMappings: curMappings as any, transformConfig: { ...(s.transformConfig ?? {}), variableMappings: curMappings } }
                                        : s,
                                    ),
                                  );
                                }}
                              >
                                <option value="STATIC_VALUE">Static Code / Text</option>
                                <option value="STEP_OUTPUT">Previous Step Output</option>
                                <option value="TARGET_CONTEXT">Target Context Metadata</option>
                              </select>

                              {vMap.sourceType === "STEP_OUTPUT" && (
                                <select
                                  style={{ width: 140 }}
                                  value={vMap.stepOrder ?? 1}
                                  onChange={(e) => {
                                    const val = Number(e.target.value);
                                    const curMappings = [...(currentStep.transformConfig?.variableMappings ?? [])];
                                    curMappings[vmIdx] = { ...curMappings[vmIdx]!, stepOrder: val };
                                    setSteps((prev) =>
                                      prev.map((s, i) =>
                                        i === editingStepIdx
                                          ? { ...s, inputMappings: curMappings as any, transformConfig: { ...(s.transformConfig ?? {}), variableMappings: curMappings } }
                                          : s,
                                      ),
                                    );
                                  }}
                                >
                                  {steps
                                    .filter((_, sIdx) => sIdx < editingStepIdx)
                                    .map((st) => (
                                      <option key={st.stepOrder} value={st.stepOrder}>
                                        Step {st.stepOrder} ({st.name})
                                      </option>
                                    ))}
                                </select>
                              )}

                              <button
                                type="button"
                                className="btn mini danger"
                                onClick={() => {
                                  const curMappings = (currentStep.transformConfig?.variableMappings ?? []).filter((_, i) => i !== vmIdx);
                                  setSteps((prev) =>
                                    prev.map((s, i) =>
                                      i === editingStepIdx
                                        ? { ...s, inputMappings: curMappings as any, transformConfig: { ...(s.transformConfig ?? {}), variableMappings: curMappings } }
                                        : s,
                                    ),
                                  );
                                }}
                              >
                                ✕
                              </button>
                            </div>

                            {vMap.sourceType === "STATIC_VALUE" && (
                              <textarea
                                rows={3}
                                style={{ marginTop: 8, width: "100%", fontFamily: "monospace", fontSize: "0.85rem" }}
                                placeholder="Paste static CSS/JS code or text snippet..."
                                value={vMap.staticValue ?? ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const curMappings = [...(currentStep.transformConfig?.variableMappings ?? [])];
                                  curMappings[vmIdx] = { ...curMappings[vmIdx]!, staticValue: val };
                                  setSteps((prev) =>
                                    prev.map((s, i) =>
                                      i === editingStepIdx
                                        ? { ...s, inputMappings: curMappings as any, transformConfig: { ...(s.transformConfig ?? {}), variableMappings: curMappings } }
                                        : s,
                                    ),
                                  );
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="row" style={{ gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
                    {!isInternal && (
                      <label className="row" style={{ gap: 6, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={currentStep.enableRetrieval}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSteps((prev) => prev.map((s, i) => (i === editingStepIdx ? { ...s, enableRetrieval: checked } : s)));
                          }}
                        />
                        <span>Enable Vector Search (Textbook PDF Retrieval)</span>
                      </label>
                    )}

                    <label className="row" style={{ gap: 6, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={currentStep.isFinalOutput ?? false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSteps((prev) =>
                            prev.map((s, i) =>
                              i === editingStepIdx
                                ? { ...s, isFinalOutput: checked }
                                : checked
                                ? { ...s, isFinalOutput: false }
                                : s,
                            ),
                          );
                        }}
                      />
                      <b>⭐ Flag as Final Content Output</b>
                      <span className="muted mini">
                        (Designates this step's output to be stored as the main content for Main App)
                      </span>
                    </label>
                  </div>

                  <div className="row end" style={{ marginTop: 20 }}>
                    <button type="button" className="btn primary" onClick={() => setEditingStepIdx(null)}>
                      Done
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="row" style={{ gap: 8, marginTop: 16 }}>
            <div style={{ marginLeft: "auto" }} className="row">
              <button className="btn" style={{ marginRight: 8 }} onClick={resetForm}>Cancel</button>
              <ActionButton className="primary" pending={isPending("pipeline-save")} onClick={() => void savePipeline()}>
                Save Pipeline
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {pipelines.loading && <p className="muted">Loading pipelines…</p>}
      {pipelines.data?.length === 0 && !creating && (
        <p className="muted">No pipelines defined for this collection yet.</p>
      )}

      {pipelines.data && pipelines.data.length > 0 && !creating && (
        <table>
          <thead>
            <tr>
              <th>Pipeline Name</th>
              <th>Version</th>
              <th>Model</th>
              <th>Steps</th>
              <th>Destination Module</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pipelines.data.map((p) => (
              <tr key={p.id}>
                <td>
                  <b>{p.name}</b>
                  {p.isActive === false && <span className="badge pending" style={{ marginLeft: 8 }}>Inactive</span>}
                  {p.autoApprove && <span className="badge badge-success" style={{ marginLeft: 8 }}>⚡ Auto-Approve</span>}
                  {p.description && <div className="muted mini">{p.description}</div>}
                </td>
                <td>v{p.version}</td>
                <td>
                  {p.defaultModel ? (
                    <span className="badge pending mini">🤖 {p.defaultModel}</span>
                  ) : (
                    <span className="badge mini" style={{ opacity: 0.8 }}>
                      🤖 Inherited ({collectionModel ?? "default"})
                    </span>
                  )}
                </td>
                <td>{p.steps?.length ?? 0} steps</td>
                <td>{p.externalModuleId ?? "—"}</td>
                <td>
                  <div className="row" style={{ gap: 6 }}>
                    <button className="btn mini" onClick={() => editPipeline(p)}>Edit</button>
                    <ActionButton
                      className="mini primary"
                      pending={isPending(`pipeline-${p.id}-clone`)}
                      onClick={() => void handleClonePipeline(p.id)}
                      title="Clone pipeline definition"
                    >
                      📋 Clone
                    </ActionButton>
                    <ActionButton
                      className="mini danger"
                      pending={isPending(`pipeline-${p.id}-delete`)}
                      onClick={() => void handleDeletePipeline(p.id, p.name)}
                      title="Delete pipeline definition"
                    >
                      🗑️ Delete
                    </ActionButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {fileDeleteModal && (
        <FileRemovalModal modal={fileDeleteModal} onClose={() => setFileDeleteModal(null)} />
      )}
      {viewingDocx && (
        <DocxViewerModal
          filename={viewingDocx.filename}
          fileUrl={viewingDocx.fileUrl}
          subtitle={viewingDocx.subtitle}
          onClose={() => setViewingDocx(null)}
        />
      )}
    </div>
  );
}


